// ============================================================
// INBOX — coach messaging with clients
// ============================================================
import { getSupabase } from './config.js';
import { emailDomainVerified } from './email-domain-status.js';
import { currentUser } from './state.js';
import { createEmailEditor, injectEditorCSS } from './email-editor.js';
import {
  POLL_MS as _POLL_MS_EXT,
  EMOJI_CATS as _EMOJI_CATS_EXT,
  formatPhone as _fmtPhone,
  formatFileSize as _fmtFileSize,
  formatRelativeTime,
  getInitials,
  escHtml,
} from './inbox/format-helpers.js';
import { highlightHtml } from './html-escape.js';
import { photoUrl, photoImgHtml } from './messages/client-photo.js';
import { renderThreadHtml, bindThreadActions, scrollToMessage } from './messages/thread-render.js';
import { openMessageMenu, closeMessageMenus } from './messages/message-menu.js';
import { getDraft, setDraft, clearDraft } from './messages/drafts.js';
import { createInboxRealtime } from './inbox/realtime.js';
import { createEmailSend } from './inbox/email-send.js';
import { createConversationActions } from './inbox/conversation-actions.js';
import { createInboxTyping } from './inbox/typing.js';
import { createInboxEmoji } from './inbox/emoji.js';
import { createInboxGroup } from './inbox/group.js';
import { showToast } from './utils.js';
import { whyNotSent, notSent, sayWhy, notSentTo, wordsToastOptions, EMAIL_FILES_LIMIT_BYTES, EMAIL_FILES_LIMIT_WORDS } from './send-failure.js';
import {
  LOAD_EARLIER_LABEL, LOAD_EARLIER_FAILED, hasOlderMessages, olderNotOnScreen, redrawThread, showLoadingEarlier,
} from './chat-history.js';
import { applyUnsent, replyTargetUnsent, unsentQuote, REPLY_TARGET_UNSENT_NOTE } from './unsent-message.js';
import * as convStore from './messages/conversation-store.js';
import { getCommsCaps, peekCommsCaps, onCommsCapsInvalidated, getCachedTwilioNumber, setCachedTwilioNumber } from './messages/comms-caps.js';
import { confirmCall, initiateCall } from './messages/client-call.js';
import { openCornerChat, cornerMessagesAllowed } from './corner-messages/index.js';
import { addUnder } from './corner-messages/add-under.js';
import { startingChannel } from './corner-messages/starting-channel.js';
import { askChannelOptions, combineWays } from './messages/channel-options.js';
import { previousView, showView, showClientProfile } from './navigation.js';
import {
  startRecording, isRecording, recordingDurationLabel, finishRecording, cancelRecording, onRecordingChange,
  busyWords, MIC_BLOCKED, RECORDING_UNSUPPORTED,
} from './messages/voice.js';

// The conversation list and the client roster live in the shared store
// (messages/conversation-store.js), so the corner messenger reads the same
// ones. _filteredConversations is this page's own view of it (the open tab).
let _filteredConversations = [];
let _activeFilter = 'all';
let _activeClientId = null;
let _activeGroupId = null;
let _activeConvType = null; // 'direct' | 'group'
let _activeMessages = [];
let _formReviewByMsg = {}; // message_id → form_videos.id (unreviewed form-check clips)
let _activeGroupMembers = []; // for group chats: [{clientId, name}]

// "Load earlier messages" (chat-history.js), 1:1 threads only. _threadSeq
// counts every conversation opened, so a page still on its way when the coach
// moves on is dropped instead of landing in the next thread. _threadTailKey is
// what was last drawn at the bottom, so a redraw can tell a NEW newest message
// (go to it) from a receipt or a reaction (leave the reader where they are).
let _hasOlder = false;
let _loadingOlder = false;
let _threadSeq = 0;
let _threadTailKey = '';

// Typing indicators (broadcast channel) — factory owns the channel
// handle + the three timers + the visibility flag. Methods are
// destructured for the existing call sites. The same channel carries
// the client's read/delivered receipt broadcasts: flip the coach's
// sent in-app bubbles live (in_app/NULL rows only — the client never
// touches the SMS/email lifecycle).
const _typing = createInboxTyping({
  onPeerRead: (at) => {
    let changed = false;
    for (const m of _activeMessages) {
      // Timestamp guard: a message sent while the client's mark-read was
      // in flight is NEWER than the receipt — don't flip it.
      if (at && new Date(m.created_at).getTime() > new Date(at).getTime()) continue;
      if (m.sender_role === 'coach' && !m.read_at && (!m.channel || m.channel === 'in_app')) {
        m.read_at = at || new Date().toISOString();
        changed = true;
      }
    }
    if (changed) renderChatMessages();
  },
  onPeerDelivered: () => {
    let changed = false;
    for (const m of _activeMessages) {
      // Undefined counts as 'sent' — just-sent rows from messages-send may
      // lack the field even though the DB default is 'sent'.
      if (m.sender_role === 'coach' && !m.read_at && (!m.delivery_status || m.delivery_status === 'sent') && (!m.channel || m.channel === 'in_app')) {
        m.delivery_status = 'delivered';
        changed = true;
      }
    }
    if (changed) renderChatMessages();
  },
  // Client toggled an emoji on a message in the open thread — patch the
  // aggregate live (the poll re-syncs the canonical counts later).
  onPeerReaction: ({ messageId, emoji, op }) => {
    const msg = _activeMessages.find(m => m.id === messageId);
    if (!msg) return;
    const reactions = [...(msg.reactions || [])];
    const idx = reactions.findIndex(r => r.emoji === emoji);
    if (idx >= 0) {
      const next = { ...reactions[idx], count: Math.max(0, reactions[idx].count + (op === 'added' ? 1 : -1)) };
      if (next.count === 0) reactions.splice(idx, 1);
      else reactions[idx] = next;
    } else if (op === 'added') {
      reactions.push({ emoji, count: 1, mine: false });
    }
    msg.reactions = reactions;
    renderChatMessages();
  },
  // A message in the open thread was unsent: by the client, or by the coach
  // on the phone app. It turns into the unsent line at once; the coach's own
  // unsend from this tab echoes back too and changes nothing.
  onUnsent: ({ messageId, at }) => {
    if (_activeConvType !== 'direct') return;
    if (applyUnsent(_activeMessages, messageId, at)) renderChatMessages();
  },
});
const subscribeActiveClientTyping = _typing.subscribeActiveClientTyping;
const unsubscribeActiveClientTyping = _typing.unsubscribeActiveClientTyping;
const broadcastCoachTyping = _typing.broadcastCoachTyping;
const renderClientTypingIndicator = _typing.renderClientTypingIndicator;
// Realtime + poll-fallback lifecycle lives in ./inbox/realtime.js. The
// factory owns the channel handle, setInterval handles, and health-
// tracking state internally. We pass getters/callbacks down so it can
// react to active-conversation state and mutate _activeMessages in place
// (the conversation list is the shared store's).
const POLL_MS = _POLL_MS_EXT;
const _realtime = createInboxRealtime({
  getActiveClientId: () => _activeClientId,
  getActiveGroupId: () => _activeGroupId,
  getActiveConvType: () => _activeConvType,
  getActiveMessages: () => _activeMessages,
  getActiveGroupMembers: () => _activeGroupMembers,
  pulseTypingIndicator: () => _typing.pulseTypingIndicator(),
  applyFilter: () => _applyFilter(),
  resolveReplyToInPlace: (msg) => _resolveReplyToInPlace(msg),
  renderChatMessages: () => renderChatMessages(),
  renderConversationsList: () => renderConversationsList(),
});
const _startMessagePoll = _realtime.startMessagePoll;
const _stopMessagePoll = _realtime.stopMessagePoll;
const _startGroupMessagePoll = _realtime.startGroupMessagePoll;
const _subscribeGroupChannel = _realtime.subscribeGroupChannel;
const _unsubscribeGroupChannel = _realtime.unsubscribeGroupChannel;
const _broadcastGroupTyping = _realtime.broadcastGroupTyping;
export const initInboxRealtime = _realtime.initInboxRealtime;

// Email-card expand/collapse + popout + inline-reply lifecycle + the
// two schedule-send flows. The factory owns no state — `_expandedEmails`,
// `_inlineEditors`, `_emailEditor`, and `_pendingScheduled` all live in
// inbox.js and are accessed via getter callbacks. The card's own buttons
// are answered by the thread's listener (_bindInboxThread); the methods
// below stay on window.* for the popout window and the inline reply, which
// call them by name.
const _emailSend = createEmailSend({
  getActiveClientId: () => _activeClientId,
  getActiveMessages: () => _activeMessages,
  getClients: () => convStore.roster(),
  getEmailEditor: () => _emailEditor,
  getExpandedEmails: () => _expandedEmails,
  getInlineEditors: () => _inlineEditors,
  pushScheduled: (s) => { _pendingScheduled.push(s); },
  renderChatMessages: () => renderChatMessages(),
  clearEmailAttachments: () => _clearEmailAttachments(),
});
window._popoutEmail = _emailSend.popoutEmail;
window._replyToEmail = _emailSend.replyToEmail;
window._sendInlineReply = _emailSend.sendInlineReply;
window._showInlineScheduleSend = _emailSend.showInlineScheduleSend;
window._cancelInlineReply = _emailSend.cancelInlineReply;
window._discardEmailDraft = _emailSend.discardEmailDraft;
window._showScheduleSend = _emailSend.showScheduleSend;
window._confirmScheduleSend = _emailSend.confirmScheduleSend;

// Conversation actions (star/archive/unread/delete).
const _convActions = createConversationActions({
  getActiveConv: () => _getActiveConv(),
  applyFilter: () => _applyFilter(),
  updateChatHeaderActions: () => _updateChatHeaderActions(),
  renderConversationsList: () => renderConversationsList(),
  getActiveFilter: () => _activeFilter,
});
window._starConversation = _convActions.starConversation;
window._archiveConversation = _convActions.archiveConversation;
window._markConversationUnread = _convActions.markConversationUnread;
window._deleteConversation = _convActions.deleteConversation;

// Reactions + emoji-picker popover. The factory owns the popover DOM
// handle; `_quickEmojis` stays in inbox.js because renderChatMessages
// reads it for the quick-react row.
const _emoji = createInboxEmoji({
  getActiveMessages: () => _activeMessages,
  setQuickEmojis: (arr) => { _quickEmojis = arr; },
  renderChatMessages: () => renderChatMessages(),
});
const _loadEmojiRanking = _emoji.loadEmojiRanking;

// Group-chat handlers (rename, members, pin, schedule, open, delete).
// All 6 methods need to be on window.* because the conv list, chat
// header, and pinned-message strip emit inline onclick attributes.
const _group = createInboxGroup({
  getConversations: () => convStore.list(),
  applyFilter: () => _applyFilter(),
  getActiveGroupId: () => _activeGroupId,
  getActiveMessages: () => _activeMessages,
  setActiveGroupId: (id) => { _activeGroupId = id; },
  setActiveClientId: (id) => { _activeClientId = id; },
  setActiveConvType: (t) => { _activeConvType = t; },
  setActiveGroupMembers: (m) => { _activeGroupMembers = m; },
  setActiveMessages: (m) => { _activeMessages = m; },
  unsubscribeActiveClientTyping: () => unsubscribeActiveClientTyping(),
  startGroupMessagePoll: (gid) => _startGroupMessagePoll(gid),
  subscribeGroupChannel: (gid) => _subscribeGroupChannel(gid),
  renderConversationsList: () => renderConversationsList(),
  renderChatMessages: () => renderChatMessages(),
  loadComposerDraft: (key) => _loadComposerDraft(key),
  popOutButtonHtml: () => _popOutButtonHtml(),
});
window._editGroupName = _group.editGroupName;
window._openGroupMembers = _group.openGroupMembers;
window._scheduleGroupMessage = _group.scheduleGroupMessage;
// Group open wraps the factory fn with composer adjustments: groups are
// in-app only, so the attach button (hidden for non-email channels on
// direct convs) surfaces here, and any stale pending file is dropped.
window.openInboxGroupConversation = async function(groupId) {
  // ⚠️ NOTHING FROM THE CHAT OPEN BEFORE COMES ALONG, and it all goes now,
  // before anything loads:
  //  - its Send Later messages (they stayed on screen inside the group, with
  //    a Cancel that worked);
  //  - a Reply picked there (_dropReplyTarget below). The group send carries
  //    the quote, so every member would see a client's words from their own
  //    chat.
  _pendingScheduled = [];
  _clearGroupFile();
  _dropReplyTarget();
  _discardPendingVoice();
  closeMessageMenus(); // a menu open on the last chat's message goes with it
  ++_threadSeq; // a client's messages still loading are not this group's
  const loaded = await _group.openInboxGroupConversation(groupId);
  document.getElementById('uc-attach-btn')?.classList.remove('hidden');
  // Only over this group's own messages: a redraw on top of "Failed to load
  // messages." would hide the failure under an empty chat.
  if (loaded) _loadScheduledForGroup(groupId);
};
window.deleteGroupConversation = _group.deleteGroupConversation;

// Reply-to state — set when coach clicks "Reply" on a message; cleared
// after send or × on the chip. Passed as replyToId in the send payload.
//
// ⚠️ IT BELONGS TO THE CONVERSATION IT WAS MADE IN. It used to survive opening
// another conversation, so the next message to Ben went out quoting Ann, and
// Ben's app drew Ann's words (and her photo) above it. Every place the open
// conversation changes calls _dropReplyTarget(); messages-send also refuses a
// quote from another thread, but the screen must never line one up.
let _replyToTarget = null; // { id, sender_role, preview, is_voice, is_attachment }

// User's top-used reaction emojis (ranked by their own history + padded
// with defaults). Fetched once on inbox init.
let _quickEmojis = ['❤️', '👍', '🔥', '👏', '💪'];

// Transient highlight — set for ~1.2s when scrolling to a referenced
// message (from a reply-floater tap), cleared after the flash.
let _highlightedMsgId = null;

// Curated emoji set for the full picker popover. Duplicated from the
// mobile app's lib/emoji-set.ts — kept in sync by intent, not code.
const _EMOJI_CATS = _EMOJI_CATS_EXT;
// _fmtPhone, _fmtFileSize, formatRelativeTime,
// getInitials all imported from ./inbox/format-helpers.js at top of file.
const _expandedEmails = new Set(); // track which email cards are expanded across re-renders
let _emailEditor = null; // standalone composer TipTap instance
let _inlineEditors = {}; // inline reply TipTap instances by msgId

// Populate email composer From/To fields from current conversation
function _populateEmailFields() {
  const fromEl = document.getElementById('inbox-email-from');
  const toEl = document.getElementById('inbox-email-to');
  if (!fromEl || !toEl) return;

  // Coach-composed emails come from coach's personal address on verified domain
  // e.g. "Clayton Nicholson <clayton@revfitcoaching.com>"
  const settings = JSON.parse(localStorage.getItem('revfit_settings') || '{}');
  const businessName = settings.emailBusinessName || 'Your Business';
  const domain = emailDomainVerified(settings) ? settings.emailDomain : '';
  // Try to get coach name from auth email prefix or business name
  const coachDisplayName = businessName;
  const emailUser = (currentUser?.email || '').split('@')[0].replace(/[^a-z]/gi, '').toLowerCase();
  const fromEmail = domain && emailUser ? `${emailUser}@${domain}` : (domain ? `notifications@${domain}` : currentUser?.email || '');
  fromEl.textContent = `${coachDisplayName} <${fromEmail}>`;

  // Get client email from conversation data
  const client = convStore.roster().find(c => c.id === _activeClientId);
  const clientEmail = client?.email || '';
  const clientName = client?.name || client?.data?.name || '';
  toEl.textContent = clientEmail ? `${clientName} <${clientEmail}>` : clientName || 'No email on file';
}

export function setInboxClients(clients) {
  convStore.setRoster(clients);
}

// ── New conversation from the inbox ──────────────────────────
// Conversations are derived from the messages table, so there is nothing to
// "create" — the picker just opens a client's thread; a zero-history client
// gets the empty state and the composer, which already work.
window._toggleNewConversation = () => {
  const panel = document.getElementById('inbox-compose-panel');
  if (!panel) return;
  const opening = panel.classList.contains('hidden');
  panel.classList.toggle('hidden');
  if (opening) {
    const search = document.getElementById('inbox-compose-search');
    if (search) { search.value = ''; search.focus(); }
    window._filterComposeClients('');
  }
};

window._filterComposeClients = (q) => {
  const list = document.getElementById('inbox-compose-list');
  if (!list) return;
  const needle = (q || '').trim().toLowerCase();
  const matches = convStore.roster()
    .map(c => ({ id: c.id, name: c.name || c.data?.name || c.email || 'Client', status: c.status }))
    .filter(c => c.status !== 'cancelled')
    .filter(c => !needle || c.name.toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 30);
  list.innerHTML = matches.length
    ? matches.map(c => `<button class="inbox-compose-item" onclick="window._startConversation('${escHtml(c.id)}')">${escHtml(c.name)}</button>`).join('')
    : '<div class="inbox-compose-empty">No matching clients</div>';
};

// ── The message menu (⋯): reactions, Reply, Copy, Unsend ──────
// The one menu the corner uses too (messages/message-menu.js). Unsend
// confirms inside it, and a refusal says why there: the server's sentence,
// or "Couldn't unsend. Try again." (this page used to toast the server's raw
// database words).
function _openMessageMenu(opener, msgId) {
  const m = _activeMessages.find(x => x.id === msgId);
  if (!m) return;
  const name = _conversationName(_activeConvType, _activeClientId, _activeGroupId);
  openMessageMenu(opener, m, {
    convType: _activeConvType,
    firstName: _activeConvType === 'group' ? name : (name.split(/\s+/)[0] || name),
    quickReactions: _quickEmojis.slice(0, 3),
  }, {
    react: (emoji) => _emoji.toggleReact(m.id, encodeURIComponent(emoji)),
    moreReactions: () => _emoji.openEmojiPicker(null, m.id, opener),
    reply: () => _replyToMsg(m.id),
    unsent: (deletedAt) => {
      // applyUnsent, not just the message: a later reply that quoted it
      // turns into "Message unsent" too. Marking twice changes nothing.
      applyUnsent(_activeMessages, m.id, deletedAt || new Date().toISOString());
      renderChatMessages();
    },
  });
}

window._startConversation = (clientId) => {
  document.getElementById('inbox-compose-panel')?.classList.add('hidden');
  window.openInboxConversation(clientId);
};

// ── Reply-to chip rendering + management ───────────────────
// Injects a slim chip above the composer's uc-wrap when the coach has
// pressed "Reply" on a message. Clicking × on the chip clears it.

function _renderReplyChip() {
  const wrap = document.getElementById('uc-wrap');
  if (!wrap) return;
  // Remove existing chip if any
  const existing = document.getElementById('inbox-reply-chip');
  if (existing) existing.remove();
  _refreshPopOut();
  if (!_replyToTarget) return;

  const preview = _replyToTarget.is_voice ? '🎤 Voice message'
    : _replyToTarget.is_attachment ? `📎 ${(_replyToTarget.preview || 'Attachment').slice(0, 150)}`
    : (_replyToTarget.preview || '').slice(0, 160);
  // A group has several clients, so there the line names the member.
  // ⚠️ THE NAME GOES IN ESCAPED, like the preview below. A client can name
  // themselves anything, and a picture tag in the name ran as page code in
  // the coach's Inbox the moment the coach pressed Reply on their message.
  const senderLabel = _replyToTarget.sender_role === 'coach' ? 'yourself' : (_replyToTarget.sender_name || 'client');

  const chip = document.createElement('div');
  chip.id = 'inbox-reply-chip';
  chip.className = 'inbox-reply-chip';
  chip.innerHTML = `
    <div class="inbox-reply-chip-body">
      <div class="inbox-reply-chip-label">Replying to ${escHtml(senderLabel)}</div>
      <div class="inbox-reply-chip-preview">${escHtml(preview)}</div>
    </div>
    <button class="inbox-reply-chip-close" onclick="window._cancelReplyTarget()" title="Cancel reply">&times;</button>
  `;
  wrap.parentElement?.insertBefore(chip, wrap);
}

function _replyToMsg(msgId) {
  const m = _activeMessages.find(x => x.id === msgId);
  if (!m) return;
  // The quote names WHAT it answers — a form check's body ("Form check ·
  // Lateral Raise") beats the raw filename; bare attachments store body =
  // filename, so the body only wins when it's real text.
  const previewBody = m.voice_url ? 'Voice message'
    : m.attachment_url
      ? ((m.body && m.body !== m.attachment_meta?.filename) ? m.body : (m.attachment_meta?.filename || 'Attachment'))
      : m.body || '';
  _replyToTarget = {
    id: m.id,
    sender_role: m.sender_role,
    sender_name: _activeConvType === 'group' ? (m.sender_name || null) : null,
    preview: previewBody.slice(0, 160),
    is_voice: !!m.voice_url,
    is_attachment: !!m.attachment_url,
  };
  _renderReplyChip();
  document.getElementById('uc-textarea')?.focus();
}

// Mark a form-check clip reviewed from the inbox thread. reviewed_at is the
// single source of truth — shared with the check-in review screen + chat
// widget, so a clip marked reviewed here stops resurfacing everywhere.
async function _markFormReviewed(formVideoId, btn) {
  if (btn) { btn.disabled = true; btn.textContent = 'Marking…'; }
  try {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    const res = await fetch('/.netlify/functions/form-video-mark-reviewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ formVideoId }),
    });
    if (!res.ok) throw new Error('Request failed');
    for (const msgId of Object.keys(_formReviewByMsg)) {
      if (_formReviewByMsg[msgId] === formVideoId) delete _formReviewByMsg[msgId];
    }
    if (btn) { btn.classList.add('cw-fv-review-btn--done'); btn.disabled = true; btn.textContent = 'Reviewed ✓'; }
  } catch {
    if (btn) { btn.disabled = false; btn.textContent = 'Mark reviewed'; }
  }
}

// The × on the chip, and every place the open conversation changes (see the
// warning at _replyToTarget): the pending quote is dropped.
function _dropReplyTarget() {
  _replyToTarget = null;
  _renderReplyChip();
}
window._cancelReplyTarget = _dropReplyTarget;

// ⚠️ A RECORDING STILL IN THE BOX BELONGS TO THE CONVERSATION IT WAS MADE IN,
// like a pending reply. Recorded for Ann and not sent, it stayed in the box
// after Ben was opened, and Send or Drop voicemail delivered Ann's voice to
// Ben. Every place that drops the reply drops this too (the floating chat
// window already did). A send under way took its own copy when Send was
// pressed, so it is never touched here.
function _discardPendingVoice() {
  // Only the Inbox's own recording: a corner chat's is never touched. A
  // recording still running takes its bar and red mic down with it (they
  // stayed up, with a Stop button that did nothing).
  if (cancelRecording(INBOX_VOICE)) _cleanupVoiceUI();
  if (!_inboxVoiceBlob && !_inboxVoiceDurLabel) return;
  _stopInboxPreview();
  _inboxVoiceBlob = null;
  _inboxVoiceDurLabel = '';
  document.getElementById('inbox-voice-attachment')?.classList.add('hidden'); _refreshPopOut();
  _cleanupVoiceUI();
}

// A reply the coach has started to a message that is now unsent is dropped,
// with a note; the words already typed stay in the box and go out as a plain
// message. Checked on every redraw of the thread, because every way a message
// turns unsent (the live event, the 90-second check, a reopened thread, the
// coach's own Unsend) ends in one.
// ⚠️ Without this the chip kept the unsent words on screen and Send still
// quoted the message (checker, 2026-10-09).
function _dropReplyToUnsent() {
  if (!replyTargetUnsent(_replyToTarget, _activeMessages)) return;
  _replyToTarget = null;
  _renderReplyChip();
  showToast(REPLY_TARGET_UNSENT_NOTE, 'info');
}

// Reactions + emoji popover live in inbox/emoji.js. The thread's buttons
// (reactions, the strip, Reply, a quote, Mark reviewed, an email card's,
// a group's pin) carry data-action and are answered by one listener on the
// Inbox's own thread (_bindInboxThread, below), never by a page function:
// the same message can be drawn in a corner window at the same time.

// ── Scroll-to-referenced-message + brief highlight flash ───
// ⚠️ INSIDE THE INBOX'S OWN THREAD. A page-wide search found the first copy
// on the page, which can be a corner window's.
function _scrollToMsg(container, msgId) {
  if (!scrollToMessage(container, msgId)) return;
  _highlightedMsgId = msgId;
  renderChatMessages();
  setTimeout(() => {
    _highlightedMsgId = null;
    renderChatMessages();
  }, 1100);
}

// One listener per thread element (the element is the page's; a test page
// may swap it), answering every button the shared renderer draws in it.
const _boundThreads = new WeakSet();
function _bindInboxThread(el) {
  if (!el || _boundThreads.has(el)) return;
  _boundThreads.add(el);
  bindThreadActions(el, {
    react: (a) => _emoji.toggleReact(a.id, a.encodedEmoji),
    'react-more': (a) => _emoji.openEmojiPicker(a.event, a.id, a.el),
    reply: (a) => _replyToMsg(a.id),
    menu: (a) => _openMessageMenu(a.el, a.id),
    'scroll-to': (a) => _scrollToMsg(a.container, a.target),
    pin: (a) => _group.pinMessage(a.id, a.pinned),
    'mark-reviewed': (a) => _markFormReviewed(a.fv, a.el),
    'toggle-email': (a) => _emailSend.toggleEmailCard(a.id),
    'popout-email': (a) => _emailSend.popoutEmail(a.id),
    'reply-email': (a) => _emailSend.replyToEmail(a.id),
  });
}

// ── Load conversations ──────────────────────────────────────
export async function loadInboxConversations() {
  try {
    // The shared store loads the list (messages/conversation-store.js); the
    // Archived tab is the only one that asks for archived chats.
    const loaded = await convStore.load({ includeArchived: _activeFilter === 'archived' });
    if ('skipped' in loaded) return; // signed out: nothing to say
    // ⚠️ A SILENT RETURN LEFT A BLANK PANE — not the empty state, not an
    // error, just whatever markup happened to be there. That is what a
    // sub-coach saw for the whole life of this feature: the Inbox opened onto
    // nothing and there was no way to tell a refusal from having no clients.
    // The store leaves the list alone on a refusal; this says why.
    if (!loaded.ok) { _showConversationsProblem(loaded.status); return; }
    // The store has already written the number of people waiting.
    _applyFilter();
    renderConversationsList();
  } catch { _showConversationsProblem(0); }
}

/** Say why the list is empty, rather than leaving the pane blank. */
function _showConversationsProblem(status) {
  const el = document.getElementById('inbox-conversations');
  if (!el) return;
  const msg = status === 403
    ? 'You do not have access to the inbox on this account. Ask the account owner.'
    : 'Could not load your conversations. Check your connection and try again.';
  el.innerHTML = `<div class="inbox-conv-empty">${msg}</div>`;
}

// ── Render conversations list ────────────────────────────────
function renderConversationsList() {
  const el = document.getElementById('inbox-conversations');
  if (!el) return;

  if (_filteredConversations.length === 0) {
    el.innerHTML = '<div class="inbox-conv-empty">No conversations yet.<br>Messages from clients will appear here.</div>';
    return;
  }

  el.innerHTML = _filteredConversations.map(c => {
    const isGroup = c.type === 'group';

    if (isGroup) {
      const initials = (c.groupName || 'GC').slice(0, 2).toUpperCase();
      const gText = c.lastMessage ? (c.lastMessage.body || _kindPreview(c.lastMessage.attachmentKind)) : null;
      const preview = gText
        ? (c.lastMessage.senderRole === 'coach' ? `You: ${gText}` : gText)
        : 'No messages yet';
      const time = c.lastMessage ? formatRelativeTime(c.lastMessage.createdAt) : '';
      const isActive = _activeConvType === 'group' && c.groupId === _activeGroupId;

      return `
        <div class="inbox-conv-item ${isActive ? 'active' : ''}" onclick="window.openInboxGroupConversation('${c.groupId}')">
          <div class="inbox-conv-avatar inbox-conv-avatar--group">${escHtml(initials)}</div>
          <div class="inbox-conv-info">
            <div class="inbox-conv-name">${escHtml(c.groupName || 'Group Chat')} <span class="inbox-conv-member-count">${c.memberCount}</span></div>
            <div class="inbox-conv-preview">${escHtml((preview || '').slice(0, 60))}</div>
          </div>
          <div class="inbox-conv-meta">
            <div class="inbox-conv-time">${time}</div>
            ${c.unreadCount > 0 ? `<div class="inbox-conv-unread">${c.unreadCount}</div>` : ''}
          </div>
        </div>`;
    }

    // Direct conversation
    const initials = getInitials(c.clientName);
    const dText = c.lastMessage.body || _kindPreview(c.lastMessage.attachmentKind) || '';
    const preview = c.lastMessage.senderRole === 'coach'
      ? `You: ${dText}` : dText;
    const time = formatRelativeTime(c.lastMessage.createdAt);
    const isActive = _activeConvType === 'direct' && c.clientId === _activeClientId;
    const starIcon = c.starred ? '<svg class="inbox-conv-star" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' : '';
    // No channel tags in the row (Clayton, 2026-10-11: "i want the pills
    // removed from the convo picker in the inbox"). The SMS, Email and Calls
    // tabs above the list still find a chat by its channels.

    return `
      <div class="inbox-conv-item ${isActive ? 'active' : ''}" onclick="window.openInboxConversation('${c.clientId}')">
        <div class="inbox-conv-avatar">${escHtml(initials)}${photoImgHtml(photoUrl(convStore.roster().find((r) => r && r.id === c.clientId)), 'inbox-conv-avatar-img')}</div>
        <div class="inbox-conv-info">
          <div class="inbox-conv-name">${starIcon}${escHtml(c.clientName)}</div>
          <div class="inbox-conv-preview">${escHtml(preview.slice(0, 60))}</div>
        </div>
        <div class="inbox-conv-meta">
          <div class="inbox-conv-time">${time}</div>
          ${c.unreadCount > 0 ? `<div class="inbox-conv-unread">${c.unreadCount}</div>` : ''}
        </div>
      </div>`;
  }).join('');
}

// Media-only last messages have an empty body — attachmentKind (from
// messages-conversations) says what was sent instead of a blank preview.
function _kindPreview(kind) {
  if (!kind) return null;
  return { voice: 'Voice message', photo: 'Photo', gif: 'GIF', video: 'Video', unsent: 'Message unsent' }[kind] || 'Attachment';
}

// ── Apply active filter ──────────────────────────────────────
function _applyFilter() {
  // ⚠️ ARCHIVED CHATS BELONG TO THE ARCHIVED TAB AND NOWHERE ELSE. Opening that
  // tab loads them along with every other chat, and the other tabs used to
  // trust the server to have left them out ("archived already excluded"),
  // which was only true of the last load. Back on All, Starred or Unread, the
  // archived chats sat in the list until the next reload a minute later.
  let list = _activeFilter === 'archived'
    ? convStore.list().filter(c => c.archived)
    : convStore.list().filter(c => !c.archived);
  switch (_activeFilter) {
    case 'unread':
      list = list.filter(c => c.unreadCount > 0 || c.manuallyUnread);
      break;
    case 'sms':
      list = list.filter(c => c.channels?.includes('sms'));
      break;
    case 'email':
      list = list.filter(c => c.channels?.includes('email'));
      break;
    case 'call':
      list = list.filter(c => c.channels?.includes('call'));
      break;
    case 'starred':
      list = list.filter(c => c.starred);
      break;
    // 'all' and 'archived' — nothing more than the split above.
  }
  // Apply search query on top
  const q = (document.getElementById('inbox-search')?.value || '').toLowerCase().trim();
  if (q) {
    list = list.filter(c => {
      if (c.type === 'group') return (c.groupName || 'Group Chat').toLowerCase().includes(q);
      return (c.clientName || '').toLowerCase().includes(q);
    });
  }
  _filteredConversations = list;
}

window._setInboxFilter = function(filter) {
  _activeFilter = filter;
  document.querySelectorAll('.inbox-filter-tab').forEach(t => t.classList.toggle('active', t.dataset.filter === filter));
  if (filter === 'archived') {
    // Need to re-fetch with archived included
    loadInboxConversations();
  } else {
    _applyFilter();
    renderConversationsList();
  }
};

// ── Filter conversations (search) ───────────────────────────
let _searchDebounceTimer = null;
let _searchResults = null;

window.filterInboxConversations = function(query) {
  _applyFilter();
  renderConversationsList();

  // Full-text message search for queries 3+ chars
  const q = (query || '').trim();
  if (q.length >= 3) {
    clearTimeout(_searchDebounceTimer);
    _searchDebounceTimer = setTimeout(() => _runMessageSearch(q), 400);
  } else {
    _searchResults = null;
    _hideSearchResults();
  }
};

async function _runMessageSearch(query) {
  try {
    const { getSupabase } = await import('./config.js');
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const res = await fetch(`/.netlify/functions/messages-search?q=${encodeURIComponent(query)}&limit=20`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    _searchResults = json.messages || [];
    _renderSearchResults(query);
  } catch {}
}

function _renderSearchResults(query) {
  let panel = document.getElementById('inbox-search-results');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'inbox-search-results';
    panel.className = 'inbox-search-results';
    const listEl = document.getElementById('inbox-conversations');
    if (listEl) listEl.parentElement.insertBefore(panel, listEl);
  }

  // ⚠️ THE SHARED escHtml, NOT A LOCAL COPY. This function kept its own
  // (textContent → innerHTML, which leaves " and ' alone) and declared it
  // BELOW the no-results line that used it, so that line threw and a search
  // that found nothing showed nothing at all.
  if (!_searchResults || _searchResults.length === 0) {
    panel.innerHTML = '<div class="inbox-search-empty">No messages matching "' + escHtml(query) + '"</div>';
    panel.classList.remove('hidden');
    return;
  }

  const _fmtTime = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  panel.innerHTML = '<div class="inbox-search-header">Messages matching "' + escHtml(query) + '"</div>'
    + _searchResults.map(m => {
      // Highlight matching text. The shared highlighter matches the text as
      // typed and escapes each piece; searching the escaped text missed any
      // search with an apostrophe in it (escHtml writes ' as &#39;).
      const highlighted = highlightHtml(m.body || m.transcript || '', query);
      return `<div class="inbox-search-item" data-client-id="${m.client_id}" onclick="window._openSearchResult('${m.client_id}')">
        <div class="inbox-search-item-name">${escHtml(m.clientName)} <span class="inbox-search-item-date">${_fmtTime(m.created_at)}</span></div>
        <div class="inbox-search-item-body">${highlighted}</div>
      </div>`;
    }).join('');

  panel.classList.remove('hidden');
}

function _hideSearchResults() {
  const panel = document.getElementById('inbox-search-results');
  if (panel) panel.classList.add('hidden');
}

window._openSearchResult = function(clientId) {
  _hideSearchResults();
  // Find conversation and open it
  const conv = convStore.list().find(c => c.clientId === clientId);
  if (conv) {
    window.openInboxConversation?.(conv.clientId);
  }
};

// Conversation star/archive/unread/delete handlers live in
// inbox/conversation-actions.js (wired into window.* at the top of
// this file). Marking a conversation read when it opens is the shared
// store's (convStore.markRead), the same for the corner messenger.

// Group-chat handlers (rename, members, pin, schedule, open, delete)
// live in inbox/group.js (wired into window.* at the top of this file).

function _getActiveConv() {
  if (_activeConvType === 'group') return convStore.list().find(c => c.type === 'group' && c.groupId === _activeGroupId);
  if (_activeConvType === 'direct') return convStore.list().find(c => c.type === 'direct' && c.clientId === _activeClientId);
  return null;
}

function _updateChatHeaderActions() {
  const conv = _getActiveConv();
  if (!conv) return;
  // By name, not as the first button: Pop out comes first, and in a group the
  // first was Rename, which starring used to light up as a star.
  const starBtn = document.querySelector('#inbox-header-actions [data-ha="star"]');
  if (starBtn) {
    starBtn.classList.toggle('active', conv.starred);
    starBtn.title = conv.starred ? 'Unstar' : 'Star';
    const svg = starBtn.querySelector('svg');
    if (svg) svg.setAttribute('fill', conv.starred ? 'currentColor' : 'none');
  }
}

// ── Pop out (spec §4.1) ──────────────────────────────────────
// The chat header's "Pop out" keeps this chat open in the corner: it opens the
// conversation's corner window on the way the Inbox is on, with the Inbox's
// draft, then goes back to the page the coach came from (navigation.js
// remembers it; a client profile through showClientProfile), or the Dashboard
// when there is none.
//
// ⚠️ THE DRAFT IS HANDED OVER, NOT LEFT FOR THE WINDOW TO FIND. A window
// already in the corner keeps what its box holds, and its next keystroke
// would save that over the words typed here.
//
// ⚠️ NOT WHILE AN EMAIL IS BEING WRITTEN. The corner's drafts are plain text,
// so an email with a subject or body typed would lose its subject and its
// formatting. The button hides then, and comes back when the email editor is
// empty or the way changes. Each change is checked a moment later, once what
// changes with it has: a scheduled email empties the editor before the
// subject (inbox/email-send.js).
const POP_OUT_TIP = 'Keep this chat open in the corner while you work';
const POP_OUT_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';

/** The composer holds an email with a subject or body typed. */
function _emailBeingWritten() {
  if (_activeConvType !== 'direct') return false;
  if (document.getElementById('uc-wrap')?.dataset?.channel !== 'email') return false;
  if (document.getElementById('uc-email-subject')?.value?.trim()) return true;
  return !!_emailEditor?.getText().trim();
}

/**
 * ⚠️ POP OUT CARRIES TYPED WORDS AND THE WAY, NOTHING ELSE. Leaving the Inbox
 * cancels a running recording and drops a waiting voice note
 * (_discardPendingVoice), and a group photo, email files or a reply chip never
 * reach the corner. So Pop out hides while the Inbox holds any of them, as it
 * does while an email is being written.
 */
function _popOutWouldDrop() {
  return _emailBeingWritten() || isRecording(INBOX_VOICE) || !!_inboxVoiceBlob
    || !!_pendingGroupFile || _groupFileUploading || _emailAttachments.length > 0 || !!_replyToTarget;
}

/** The header's Pop out; none for someone the corner is not for (§9). */
function _popOutButtonHtml() {
  if (!cornerMessagesAllowed()) return '';
  return `<button type="button" class="inbox-ha-btn${_popOutWouldDrop() ? ' hidden' : ''}" data-ha="pop-out" aria-label="Pop out" title="${POP_OUT_TIP}">${POP_OUT_ICON}</button>`;
}

let _popOutCheckQueued = false;
function _refreshPopOut() {
  if (_popOutCheckQueued) return;
  _popOutCheckQueued = true;
  queueMicrotask(() => {
    _popOutCheckQueued = false;
    document.querySelector('#inbox-header-actions [data-ha="pop-out"]')?.classList.toggle('hidden', _popOutWouldDrop());
  });
}

function _popOut() {
  const isGroup = _activeConvType === 'group';
  const target = isGroup ? (_activeGroupId && { groupId: _activeGroupId })
    : (_activeConvType === 'direct' && _activeClientId && { clientId: _activeClientId });
  if (!target || _popOutWouldDrop()) return;
  const draft = document.getElementById('uc-textarea')?.value || '';
  const channel = isGroup ? undefined : (document.getElementById('uc-wrap')?.dataset?.channel || 'in_app');
  // Opened before leaving: a corner that did not start says why, and the
  // coach stays here with the chat.
  if (!openCornerChat(target, { draft, channel, focus: false })) return;
  const back = previousView();
  if (back?.view === 'client' && back.clientId) showClientProfile(back.clientId);
  else showView(back && back.view !== 'inbox' ? back.view : 'dashboard');
  // The cursor into its box, now that the corner shows (it hides on this page).
  openCornerChat(target);
}
// One listener for both headers (this file's and inbox/group.js's).
document.addEventListener('click', (e) => {
  if (e.target?.closest?.('#inbox-header-actions [data-ha="pop-out"]')) _popOut();
});

// ── Open conversation ────────────────────────────────────────
// opts (C10): { channel, draft } from the corner's "Open in Inbox" and
// "Write email": the window's way, and its draft. Without a way the Inbox
// starts the way the client last wrote (D4, like the corner), with the
// conversation's own draft.
/**
 * Put the reply on this way. An email starts from the conversation's draft.
 * @param {'sms' | 'email'} ch
 * @param {string} draftKey
 */
function _startReplyOn(ch, draftKey) {
  window._setComposerChannel?.(ch);
  const carried = getDraft(draftKey);
  if (ch === 'email' && carried && document.getElementById('uc-wrap')?.dataset?.channel === 'email') {
    _emailSeededFrom = { key: draftKey, text: carried };
    _emailSend.carryDraftIntoEditor(carried);
  }
}

// The conversation (its _threadSeq) in which the coach last picked a way from
// the channel menu: an answer that arrives after that pick leaves it alone.
let _wayPickedIn = -1;
document.addEventListener('click', (e) => {
  if (e.target?.closest?.('.uc-channel-option')) _wayPickedIn = _threadSeq;
});

/**
 * Start the reply the way this client last wrote (D4): the same two answers
 * and the one rule the corner uses. A way the coach cannot use is never picked
 * (the reply stays In-app, quietly). The answer comes after the conversation
 * opens, so it changes nothing once the coach has picked a way or moved on.
 * @param {string} clientId
 * @param {number} seq the conversation's _threadSeq when it opened
 * @param {string} draftKey
 */
async function _startTheWayTheClientWrote(clientId, seq, draftKey) {
  const [answer, caps] = await Promise.all([askChannelOptions(clientId), getCommsCaps().catch(() => null)]);
  if (seq !== _threadSeq || _wayPickedIn === seq || _activeClientId !== clientId) return;
  const start = startingChannel({
    lastClientChannel: answer?.lastClientChannel || null,
    hasPhone: answer?.hasPhone === true,
    available: combineWays(answer, caps),
  });
  if (start.sendDisabled || start.channel === 'in_app') return;
  _startReplyOn(start.channel, draftKey);
}

window.openInboxConversation = async function(clientId, opts = {}) {
  _activeClientId = clientId;
  _activeGroupId = null;
  _activeConvType = 'direct';
  _activeGroupMembers = [];
  // ⚠️ The last chat's messages go now, not when this client's arrive (see
  // the same line in inbox/group.js).
  _activeMessages = [];
  _hasOlder = false;
  _loadingOlder = false;
  const seq = ++_threadSeq;
  _convChannelFilter = 'all'; // a filter never follows you between conversations
  // ⚠️ THE CHAT OPEN BEFORE LEAVES NOTHING BEHIND. Its Send Later messages go
  // now, before anything loads: a group's stayed on screen here, with a
  // Cancel that worked, until this client's own list arrived. A Reply picked
  // there goes too (_dropReplyTarget below), or this client's message would
  // go out quoting someone else's words.
  _pendingScheduled = [];
  _unsubscribeGroupChannel(); // leaving any group → drop its reaction channel
  subscribeActiveClientTyping(clientId);
  const conv = convStore.list().find(c => c.type === 'direct' && c.clientId === clientId);
  const clientName = conv?.clientName || 'Client';

  // Reset unified composer to in_app channel (this also re-hides the
  // attach button that group conversations surface).
  window._setComposerChannel?.('in_app');
  _clearGroupFile();
  _dropReplyTarget();
  _discardPendingVoice();
  closeMessageMenus(); // a menu open on the last chat's message goes with it
  const ucSubject = document.getElementById('uc-email-subject');
  if (ucSubject) ucSubject.value = '';
  // ⚠️ THE BOX HOLDS THIS CONVERSATION'S DRAFT (messages/drafts.js), shared
  // with the corner messenger: never emptied, and never the last one's words.
  const draftKey = `c:${clientId}`;
  if (typeof opts.draft === 'string' && opts.draft.trim()) setDraft(draftKey, opts.draft);
  _loadComposerDraft(draftKey);
  _emailSend.carryDraftIntoEditor(null); // a draft waiting for another client's editor
  _emailSeededFrom = null;
  if (_emailEditor) _emailEditor.clear();
  // The way the corner's window was on. A way the coach cannot use yet is
  // refused by _setComposerChannel (it says why), and the box stays In-app.
  if (opts.channel === 'sms' || opts.channel === 'email') _startReplyOn(opts.channel, draftKey);
  // Opened here (not from a corner window): the way the client last wrote.
  else if (!opts.channel) void _startTheWayTheClientWrote(clientId, seq, draftKey);

  // Show chat pane
  const listPane = document.getElementById('inbox-list-pane');
  const chatPane = document.getElementById('inbox-chat-pane');
  listPane?.classList.add('conv-open');
  chatPane?.classList.remove('hidden');

  // Header
  const header = document.getElementById('inbox-chat-header');
  const isStarred = conv?.starred || false;
  const isArchived = conv?.archived || false;
  header.innerHTML = `
    <button class="inbox-chat-back" onclick="window.closeInboxConversation()">&lsaquo; Back</button>
    <a class="inbox-chat-name inbox-chat-name--link" href="#" onclick="event.preventDefault();window.showClientProfile('${clientId}')">${escHtml(clientName)}</a>
    <div class="inbox-header-actions" id="inbox-header-actions">
      ${_popOutButtonHtml()}
      <button class="inbox-ha-btn${isStarred ? ' active' : ''}" data-ha="star" onclick="window._starConversation()" title="${isStarred ? 'Unstar' : 'Star'}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="${isStarred ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
      </button>
      <button class="inbox-ha-btn" onclick="window._archiveConversation()" title="${isArchived ? 'Unarchive' : 'Archive'}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
      </button>
      <button class="inbox-ha-btn" onclick="window._markConversationUnread()" title="Mark as unread">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
      </button>
      <button class="inbox-ha-btn inbox-ha-btn--danger" onclick="window._deleteConversation()" title="Delete conversation">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
      </button>
      <button class="inbox-call-btn" onclick="window._confirmCall()" title="Call client">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
      </button>
    </div>`;

  // Loading state
  const messagesEl = document.getElementById('inbox-chat-messages');
  messagesEl.innerHTML = '<div style="padding:40px;text-align:center;color:var(--text-muted);">Loading…</div>';

  // Highlight active conversation
  renderConversationsList();

  // Fetch messages
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  let loaded = false;
  try {
    const res = await fetch('/.netlify/functions/messages-list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ clientId }),
    });
    if (!res.ok) throw new Error('Failed');
    const json = await res.json();
    // ⚠️ Another chat was opened while this loaded. Its messages, its read
    // marks and its poll are its own: this answer replaced them.
    if (seq !== _threadSeq) return;
    _activeMessages = json.messages || [];
    _linkClicks = json.link_clicks || {};
    _hasOlder = hasOlderMessages(json);

    // Unreviewed form-check clips → "Mark reviewed" buttons on their messages.
    try {
      const fvRes = await fetch('/.netlify/functions/form-video-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ clientId }),
      });
      const fvJson = fvRes.ok ? await fvRes.json() : { videos: [] };
      _formReviewByMsg = {};
      for (const v of fvJson.videos || []) {
        if (v.message_id) _formReviewByMsg[v.message_id] = v.id;
      }
    } catch { _formReviewByMsg = {}; }

    renderChatMessages();
    loaded = true;

    // Mark as read. Every number drops at once, and the shared store tells
    // the server (and clears "Mark as unread" if it was set).
    await convStore.markRead(`c:${clientId}`);
    renderConversationsList();
  } catch {
    if (seq !== _threadSeq) return;
    // Only when the messages never came: a failed read mark after they are on
    // screen used to wipe them with this notice.
    if (!loaded) messagesEl.innerHTML = '<div style="padding:40px;text-align:center;color:var(--text-muted);">Failed to load messages.</div>';
  }

  // Fetch pending scheduled emails for this conversation, over its own
  // messages only (a redraw over "Failed to load messages." hid the failure).
  if (loaded) _loadScheduledForClient(clientId, session?.access_token);

  // Focus input
  document.getElementById('uc-textarea')?.focus();

  // Poll for new messages
  _startMessagePoll(clientId);
};

// ── Load earlier messages (1:1) ──────────────────────────────
// The page in front of the oldest message on screen, by messages-list's
// `before` cursor. skipMarkRead: reading back through old messages is not
// opening the conversation, and the open already marked everything read.
window._loadEarlierMessages = async function() {
  const clientId = _activeClientId;
  const before = _activeMessages[0]?.created_at;
  if (_activeConvType !== 'direct' || !clientId || !before || !_hasOlder || _loadingOlder) return;
  const seq = _threadSeq;
  const stillHere = () => seq === _threadSeq && _activeConvType === 'direct' && _activeClientId === clientId;
  const el = document.getElementById('inbox-chat-messages');
  _loadingOlder = true;
  showLoadingEarlier(el);
  try {
    const { data: { session } } = await getSupabase().auth.getSession();
    const res = await fetch('/.netlify/functions/messages-list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ clientId, before, skipMarkRead: true }),
    });
    if (!res.ok) throw new Error(`messages-list ${res.status}`);
    const json = await res.json();
    // ⚠️ THE COACH MAY HAVE MOVED ON. Another conversation opened (a group
    // chat does not count one up, hence the second half) or this one closed
    // while the page was on its way: it belongs to nothing on screen.
    if (!stillHere()) return;
    _activeMessages.unshift(...olderNotOnScreen(_activeMessages, json.messages));
    Object.assign(_linkClicks, json.link_clicks || {});
    _hasOlder = hasOlderMessages(json);
    _loadingOlder = false;
    renderChatMessages({ olderAdded: true });
  } catch {
    if (!stillHere()) return;
    _loadingOlder = false;
    renderChatMessages();
    showToast(LOAD_EARLIER_FAILED, 'error');
  }
};

// openInboxGroupConversation and deleteGroupConversation live in
// inbox/group.js (wired to window.* at top of file).

// ── Close conversation (mobile) ──────────────────────────────
window.closeInboxConversation = function() {
  _stopMessagePoll();
  closeMessageMenus();
  unsubscribeActiveClientTyping();
  _unsubscribeGroupChannel();
  _clearGroupFile();
  _dropReplyTarget();
  _discardPendingVoice();
  document.getElementById('uc-attach-btn')?.classList.add('hidden');
  _activeClientId = null;
  _activeGroupId = null;
  _activeConvType = null;
  _activeGroupMembers = [];
  _hasOlder = false;
  _loadingOlder = false;
  _threadSeq++;
  document.getElementById('inbox-list-pane')?.classList.remove('conv-open');
  document.getElementById('inbox-chat-pane')?.classList.add('hidden');
  renderConversationsList();
};

// Typing indicators (factory at top of file). The composer-input
// listener stays here because its guard (_activeConvType / _activeClientId)
// is parent state — the typing factory just owns the channel + timers.
// Grow the composer to fit what's being typed, up to the CSS max-height (the
// browser clamps it there and overflow-y takes over). Height must be reset to
// auto first or scrollHeight can only ever report the current height and the
// field never shrinks back when text is deleted.
//
// `offsetHeight - clientHeight` adds the borders back: scrollHeight is
// content + padding, and the field is border-box, so without it the field
// lands a couple of pixels short and shows a scrollbar one line early.
function _autogrowComposer(el) {
  const ta = el || document.getElementById('uc-textarea');
  if (!ta) return;
  ta.style.height = 'auto';
  ta.style.height = `${ta.scrollHeight + (ta.offsetHeight - ta.clientHeight)}px`;
}

// Back to one line. Called after send and whenever the composer is emptied —
// clearing `value` alone leaves the inline height from the last message.
function _resetComposerHeight() {
  const ta = document.getElementById('uc-textarea');
  if (ta) ta.style.height = '';
}

// ── Drafts (messages/drafts.js) ──────────────────────────────
// One per conversation, shared with the corner messenger. The box shows the
// open conversation's draft; typing saves it; a send that goes clears it.
function _draftKeyOf(convType, clientId, groupId) {
  if (convType === 'group' && groupId) return `g:${groupId}`;
  if (convType === 'direct' && clientId) return `c:${clientId}`;
  return null;
}

/** Put a conversation's draft in the box (or empty it), sized to the words. */
function _loadComposerDraft(key) {
  const ta = document.getElementById('uc-textarea');
  if (!ta) return;
  ta.value = getDraft(key);
  if (ta.value) _autogrowComposer(ta);
  else _resetComposerHeight();
}

// The email "Write email" seeded from a conversation's draft: when that email
// goes, the draft it carried goes too.
let _emailSeededFrom = null; // { key, text }

document.addEventListener('input', (e) => {
  if (e.target?.id === 'uc-email-subject') { _refreshPopOut(); return; }
  if (e.target?.id !== 'uc-textarea') return;
  _autogrowComposer(e.target);
  // Saved as it is typed, so the corner and a return to this chat have it.
  const draftKey = _draftKeyOf(_activeConvType, _activeClientId, _activeGroupId);
  if (draftKey) setDraft(draftKey, e.target.value);
  if (_activeConvType === 'direct' && _activeClientId) {
    _typing.debouncedBroadcastCoachTyping();
  } else if (_activeConvType === 'group' && _activeGroupId) {
    // Group members' apps show "Coach is typing…" (throttled below).
    _debouncedGroupTyping();
  }
});
let _groupTypingTimer = null;
function _debouncedGroupTyping() {
  if (_groupTypingTimer) return;
  _broadcastGroupTyping();
  _groupTypingTimer = setTimeout(() => { _groupTypingTimer = null; }, 3000);
}

// ── Render chat messages ─────────────────────────────────────
// Each message is drawn by the shared renderer (messages/thread-render.js):
// day lines, runs, bubbles, reactions, the hover strip, time and status.

// ── In-conversation channel filter ──────────────────────────
// 'all' | 'in_app' | 'sms' | 'email' | 'call' | 'event'. Channel tabs include
// that channel's event rows (an SMS event IS sms traffic); the Events tab
// isolates the timeline. Reset on every conversation open.
let _convChannelFilter = 'all';

// { code: { first_clicked_at, click_count } } from messages-list — click
// status for every /l/ and /c/ short link in the visible thread ("Link
// opened", drawn by messages/thread-render.js).
let _linkClicks = {};

function _msgBucket(m) {
  if (m.source === 'event') return 'event';
  return m.channel || 'in_app';
}

function _matchesConvFilter(m) {
  if (_convChannelFilter === 'all') return true;
  if (_convChannelFilter === 'event') return m.source === 'event';
  return (m.channel || 'in_app') === _convChannelFilter;
}

window._setConvChannelFilter = (ch) => {
  _convChannelFilter = ch;
  renderChatMessages();
};

// The strip only appears when the conversation actually spans more than one
// bucket — a pure in-app thread stays clean.
function _renderConvFilterStrip() {
  const strip = document.getElementById('inbox-conv-filter');
  if (!strip) return;
  const buckets = new Set(_activeMessages.map(_msgBucket));
  if (buckets.size < 2) {
    strip.classList.add('hidden');
    if (_convChannelFilter !== 'all') _convChannelFilter = 'all';
    return;
  }
  const LABELS = { all: 'All', in_app: 'In-app', sms: 'SMS', email: 'Email', call: 'Calls', event: 'Events' };
  const order = ['all', 'in_app', 'sms', 'email', 'call', 'event'];
  strip.innerHTML = order
    .filter(k => k === 'all' || buckets.has(k))
    .map(k => `<button class="inbox-filter-tab ${_convChannelFilter === k ? 'active' : ''}" onclick="window._setConvChannelFilter('${k}')">${LABELS[k]}</button>`)
    .join('');
  strip.classList.remove('hidden');
}

// opts.olderAdded — an earlier page was just put above (chat-history.js).
function renderChatMessages(opts = {}) {
  const el = document.getElementById('inbox-chat-messages');
  if (!el) return;
  _bindInboxThread(el);

  _dropReplyToUnsent();

  _renderConvFilterStrip();

  // A different conversation, filter or newest message → go to the newest
  // message. Anything else (a receipt, a reaction, an older page) leaves a
  // reader who has scrolled up where they are.
  const tailKey = `${_activeConvType}:${_activeClientId || _activeGroupId}|${_convChannelFilter}|${_activeMessages[_activeMessages.length - 1]?.id || ''}`;
  const followNewest = tailKey !== _threadTailKey;
  _threadTailKey = tailKey;

  if (_activeMessages.length === 0) {
    el.innerHTML = '<div class="inbox-chat-empty">No messages yet. Send the first message!</div>';
    return;
  }

  // "Load earlier messages" sits above the oldest message on screen.
  let html = (_activeConvType === 'direct' && _hasOlder)
    ? `<div class="chat-load-earlier"><button type="button" class="chat-load-earlier-btn" onclick="window._loadEarlierMessages()">${LOAD_EARLIER_LABEL}</button></div>`
    : '';

  // Pinned messages banner (group chats only)
  if (_activeConvType === 'group') {
    const pinned = _activeMessages.filter(m => m.pinned_at);
    if (pinned.length > 0) {
      html += '<div class="inbox-pinned-section">';
      html += '<div class="inbox-pinned-header"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg> Pinned</div>';
      pinned.forEach(m => {
        const senderName = m.sender_role === 'coach' ? 'You' : (m.sender_name || 'Client');
        // sender_name is the client's own name, which they type themselves.
        html += `<div class="inbox-pinned-msg">
          <div class="inbox-pinned-msg-body">${escHtml((m.body || '').substring(0, 200))}</div>
          <div class="inbox-pinned-msg-meta">${escHtml(senderName)} <button type="button" class="inbox-pinned-unpin" data-action="pin" data-mid="${escHtml(m.id)}" data-pinned="true" title="Unpin">&times;</button></div>
        </div>`;
      });
      html += '</div>';
    }
  }

  const _visibleMessages = _convChannelFilter === 'all'
    ? _activeMessages
    : _activeMessages.filter(_matchesConvFilter);
  if (_visibleMessages.length === 0 && _activeMessages.length > 0) {
    el.innerHTML = html + '<div class="inbox-chat-empty">Nothing on this channel in this conversation.</div>';
    return;
  }

  // Each message, drawn by the shared renderer (messages/thread-render.js),
  // the same one the corner messenger uses.
  const emailClient = convStore.roster().find(c => c.id === _activeClientId);
  html += renderThreadHtml(_visibleMessages, {
    idPrefix: 'inbox',
    actions: 'strip',
    convType: _activeConvType,
    groupMembers: _activeGroupMembers,
    quickEmojis: _quickEmojis,
    formReviewByMsg: _formReviewByMsg,
    linkClicks: _linkClicks,
    expandedEmails: _expandedEmails,
    highlightedId: _highlightedMsgId,
    emailTo: { name: emailClient?.name || emailClient?.data?.name || '', email: emailClient?.email || '' },
  });

  // Append inline scheduled messages at the bottom
  html += _buildInlineScheduledHtml();

  // Don't auto-scroll to bottom if an email card is expanded — user is reading
  redrawThread(el, html, {
    olderAdded: !!opts.olderAdded,
    holdStill: _expandedEmails.size > 0,
    followNewest,
  });
}

// ── Email card UI + send/schedule actions live in inbox/email-send.js
// (wired into window.* at the top of this file via the createEmailSend
// factory). `_resolveReplyToInPlace` stays here because realtime.js also
// uses it as a callback — it's generic reply hydration, not email-specific.

// ── Drop voicemail (chunk 9) ────────────────────────────────
// Uploads the recorded voice blob to Supabase Storage, then asks
// twilio-voicemail-drop to place a call with AMD set to wait for the
// voicemail beep before playing. If a human answers, the TwiML
// callback hangs up immediately so we don't disturb them.
window._dropVoicemail = async function() {
  if (!_inboxVoiceBlob) {
    showToast('Record a voice message first, then choose Drop voicemail.', 'error');
    return;
  }
  if (!_activeClientId) {
    showToast('Open a client conversation first.', 'error');
    return;
  }
  // Voicemail drop is phone-based — confirm with the coach if they're
  // currently on a non-SMS channel (in_app / email).
  const ch = document.getElementById('uc-wrap')?.dataset?.channel || 'in_app';
  if (ch !== 'sms') {
    const ok = confirm('Drop voicemail to this client\'s phone?\n\nThis places a real phone call. The recording plays only if it reaches voicemail; if a human answers, it hangs up immediately.');
    if (!ok) return;
  }

  // ⚠️ WHO THIS CALL IS FOR IS DECIDED NOW, as in sendInboxMessage. The
  // recording uploads first, and this used to look at the open client again
  // after it: drop a voicemail for Ann, open Ben before the upload ends, and
  // Ben's phone rang with Ann's recording.
  const clientId = _activeClientId;
  const blob = _inboxVoiceBlob;
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    showToast('Sign in expired — refresh and try again.', 'error');
    return;
  }

  try {
    const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    const path = `${session.user.id}/voicemail-drops/${Date.now()}_${Math.random().toString(36).slice(2, 7)}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('coach-videos')
      .upload(path, blob, { contentType: blob.type, upsert: false });
    if (upErr) throw new Error(upErr.message);

    const { data: signed, error: signErr } = await supabase.storage
      .from('coach-videos')
      .createSignedUrl(path, 7 * 24 * 60 * 60); // 7d is plenty — Twilio fetches once
    if (signErr) throw new Error(signErr.message);

    const res = await fetch('/.netlify/functions/twilio-voicemail-drop', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ clientId, recordingUrl: signed.signedUrl }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Voicemail drop failed');

    // Clear the recording from the composer, if it is still this one. A new
    // recording made since (for whoever is open now) stays.
    if (_inboxVoiceBlob === blob) {
      _inboxVoiceBlob = null;
      _inboxVoiceDurLabel = '';
      document.getElementById('inbox-voice-attachment')?.classList.add('hidden'); _refreshPopOut();
    }
    showToast('Voicemail drop placed. It will play only if the call reaches voicemail.', 'success');
  } catch (err) {
    console.error('[voicemail-drop]', err);
    showToast('Voicemail drop failed: ' + err.message, 'error');
  }
};

// ── Schedule message (in-app / SMS) ─────────────────────────
window._showScheduleMessage = function() {
  // ⚠️ A GROUP HAS NO CLIENT. This used to check for one and so said "Type a
  // message first" in every group, with the words right there in the box.
  // A group is scheduled by its own id, and groups are in-app only.
  const isGroup = _activeConvType === 'group';
  const target = isGroup ? (_activeGroupId && { groupId: _activeGroupId }) : (_activeClientId && { clientId: _activeClientId });
  const selectedChannel = isGroup ? 'in_app' : (document.getElementById('uc-wrap')?.dataset?.channel || 'in_app');
  const isEmail = selectedChannel === 'email';
  let body = '';
  let html = '';
  if (isEmail && _emailEditor) {
    body = _emailEditor.getText().trim();
    html = _emailEditor.getHTML();
  } else {
    body = document.getElementById('uc-textarea')?.value?.trim() || '';
  }
  if (!body || !target) { showToast('Type a message first', 'error'); return; }
  // ⚠️ THE DRAFT GOES WITH THE SCHEDULE. Only the box was emptied, and every
  // open of this chat (and the corner) put the scheduled words back, ready to
  // send now as well as at the scheduled time. Read now, like the words.
  const draftKey = _draftKeyOf(_activeConvType, _activeClientId, _activeGroupId);
  const seededDraft = isEmail && _emailSeededFrom?.key === draftKey ? _emailSeededFrom.text : null;
  // The files are taken now, with the words, and ride with the schedule; the
  // cron sends what the scheduled row holds (process-scheduled-emails).
  // ⚠️ The schedule request was built without them and the composer was then
  // emptied, so a scheduled email lost its files and nothing said so.
  const scheduleFiles = isEmail ? _emailAttachments.slice() : [];
  // Refused before the coach picks a time: the schedule request carries the
  // files the same way Send does, under the same limit.
  const filesTooBig = _emailFilesTooBig(scheduleFiles);
  if (filesTooBig) { showToast(filesTooBig, 'error'); return; }
  const channelLabel = selectedChannel === 'sms' ? 'SMS' : 'message';

  // Default: 1 hour from now, formatted in the COACH'S browser-local
  // timezone (datetime-local inputs interpret values as local time, so
  // toISOString().slice(...) was off by the UTC offset). Build the
  // YYYY-MM-DDTHH:MM string from local components instead.
  const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${defaultDate.getFullYear()}-${pad(defaultDate.getMonth() + 1)}-${pad(defaultDate.getDate())}T${pad(defaultDate.getHours())}:${pad(defaultDate.getMinutes())}`;
  const tzLabel = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
      const tz = parts.find(p => p.type === 'timeZoneName')?.value;
      const fullTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${fullTz} (${tz})` : fullTz;
    } catch { return ''; }
  })();

  const overlay = document.createElement('div');
  overlay.className = 'inbox-email-modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  overlay.innerHTML = `
    <div class="inbox-email-modal" style="max-width:380px;">
      <div class="inbox-email-modal-header">
        <div class="inbox-email-modal-subject">Schedule ${channelLabel}</div>
        <button class="inbox-email-modal-close" onclick="this.closest('.inbox-email-modal-overlay').remove()">&times;</button>
      </div>
      <div style="padding:20px;">
        <p style="font-size:13px;color:var(--text-muted);margin-bottom:14px;">Choose when this ${channelLabel} should be sent.</p>
        <input type="datetime-local" id="schedule-msg-datetime" value="${dateStr}" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:6px;font-size:14px;color:var(--text);background:var(--bg-hover);" />
        ${tzLabel ? `<div style="font-size:11px;color:var(--text-muted);margin-top:6px;">Time zone: ${tzLabel}</div>` : ''}
        <div style="display:flex;gap:8px;margin-top:16px;">
          <button class="btn-primary" style="flex:1;padding:10px;font-size:13px;font-weight:700;" id="schedule-msg-confirm">Schedule</button>
          <button class="inbox-email-discard-btn" style="flex:1;padding:10px;font-size:13px;" onclick="this.closest('.inbox-email-modal-overlay').remove()">Cancel</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  document.getElementById('schedule-msg-confirm')?.addEventListener('click', async () => {
    const scheduledFor = new Date(document.getElementById('schedule-msg-datetime').value).toISOString();
    // Read before the composer is cleared below: the queue's copy read it
    // afterwards, so a scheduled email showed in the thread with no subject.
    const subject = isEmail ? (document.getElementById('uc-email-subject')?.value?.trim() || '') : '';
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/scheduled-email-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          ...target, body, html: html || undefined,
          subject: subject || undefined,
          channel: selectedChannel, scheduledFor,
          attachments: scheduleFiles.length ? _attachmentsForRequest(scheduleFiles) : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      overlay.remove();
      // Clear composer
      if (isEmail) {
        const sf = document.getElementById('uc-email-subject'); if (sf) sf.value = '';
        if (_emailEditor) _emailEditor.clear();
        _clearEmailAttachments();
      } else {
        const ta = document.getElementById('uc-textarea'); if (ta) { ta.value = ''; _resetComposerHeight(); }
      }
      // Never words the coach typed into the draft since (the corner shares it).
      if (!isEmail && getDraft(draftKey).trim() === body) clearDraft(draftKey);
      // An email "Write email" seeded from the draft: the draft has gone, as on Send.
      if (seededDraft != null && getDraft(draftKey) === seededDraft) {
        clearDraft(draftKey);
        if (_emailSeededFrom?.key === draftKey) _emailSeededFrom = null;
        const ta = document.getElementById('uc-textarea');
        if (ta && ta.value === seededDraft) { ta.value = ''; _resetComposerHeight(); }
      }
      // Add to inline scheduled immediately
      _pendingScheduled.push({
        id: json.id,
        clientId: target.clientId || null,
        groupId: target.groupId || null,
        subject,
        body,
        channel: selectedChannel,
        scheduledFor,
        status: 'pending',
      });
      renderChatMessages();
      const dt = new Date(scheduledFor);
      const timeLabel = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      showToast(`${channelLabel} scheduled for ${timeLabel}`, 'success');
    } catch (err) { alert('Failed: ' + err.message); }
  });
};

// ── Send message ─────────────────────────────────────────────

// Who a conversation is with, in words, for a toast about a send that failed
// after the coach had moved on (notSentTo in send-failure.js).
function _conversationName(convType, clientId, groupId) {
  if (convType === 'group') {
    return convStore.list().find(c => c.type === 'group' && c.groupId === groupId)?.groupName || 'the group';
  }
  const conv = convStore.list().find(c => c.type === 'direct' && c.clientId === clientId);
  const client = convStore.roster().find(c => c.id === clientId);
  return conv?.clientName || client?.name || client?.data?.name || 'this client';
}

window.sendInboxMessage = async function() {
  const selectedChannel = document.getElementById('uc-wrap')?.dataset?.channel || 'in_app';
  const isEmail = selectedChannel === 'email';

  // Read from correct composer
  const input = isEmail ? null : document.getElementById('uc-textarea');
  const hasVoice = !!_inboxVoiceBlob;
  let textBody = '';
  let htmlBody = '';
  if (isEmail && _emailEditor) {
    textBody = _emailEditor.getText().trim();
    htmlBody = _emailEditor.getHTML();
  } else if (input) {
    textBody = input.value.trim();
  }

  // ⚠️ WHO THIS SEND IS FOR IS DECIDED NOW, WHEN SEND IS PRESSED. A voice note
  // uploads first and can take seconds, and this used to look at the open
  // conversation again after it: record a voice note for Ann, press Send, open
  // Ben before the upload ends, and the voice note AND the text typed for Ann
  // went to Ben. Every request below uses these, never the live values, and
  // the screen (the thread, the box) is only touched if this same
  // conversation is still the one open.
  const convType = _activeConvType;
  const clientId = _activeClientId;
  const groupId = _activeGroupId;
  const replyTarget = _replyToTarget;
  const stillOpen = () => _activeConvType === convType && _activeClientId === clientId && _activeGroupId === groupId;
  const draftKey = _draftKeyOf(convType, clientId, groupId);
  const seededDraft = isEmail && _emailSeededFrom?.key === draftKey ? _emailSeededFrom.text : null;

  // Group attachment (photo/GIF/file) — counts as content like voice does.
  const groupFile = convType === 'group' ? _pendingGroupFile : null;
  const hasGroupFile = !!groupFile;
  if (convType === 'group' && _groupFileUploading) {
    showToast('Attachment still uploading — one sec…', 'error');
    return;
  }
  if (!textBody && !hasVoice && !hasGroupFile) return;
  if (!clientId && !groupId) return;
  // Who the message was for, in words, for a toast if it fails after the
  // coach has moved on (the words ride in it, and go back to that
  // conversation's draft).
  const sentToName = _conversationName(convType, clientId, groupId);

  // Capture the attached files before clearing.
  // ⚠️ The files were read AFTER the clear below, from the list it had just
  // emptied, so every Inbox email went out with no files and nothing said so.
  const emailAttachments = isEmail ? _emailAttachments.slice() : [];
  // More than one request can carry is refused here, in words, with nothing
  // cleared, so the coach can remove a file and press Send again.
  const filesTooBig = _emailFilesTooBig(emailAttachments);
  if (filesTooBig) { showToast(filesTooBig, 'error'); return; }

  const btn = document.getElementById('uc-send-btn');
  btn.disabled = true;
  // Capture subject before clearing
  const subjectField = document.getElementById('uc-email-subject');
  const emailSubject = subjectField?.value?.trim() || '';
  // Clearing `value` alone leaves the inline height autogrow set while typing,
  // so the composer stayed tall over an empty field after every send.
  if (input) { input.value = ''; _resetComposerHeight(); clearDraft(draftKey); }
  if (isEmail) {
    if (subjectField) subjectField.value = '';
    if (_emailEditor) _emailEditor.clear();
    _clearEmailAttachments();
  }

  // The recording is this send's from now on, taken before any wait, so
  // opening another conversation (which throws away a recording still in the
  // box) can never take it from a send already under way.
  const voiceBlob = hasVoice ? _inboxVoiceBlob : null;
  const voiceDurLabel = hasVoice ? _inboxVoiceDurLabel : '';
  if (hasVoice) { _inboxVoiceBlob = null; _inboxVoiceDurLabel = ''; }

  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  let lastCreatedAt = null;
  let previewBody = textBody;

  // Upload and send voice if attached
  if (hasVoice) {
    _stopInboxPreview();
    const blob = voiceBlob;
    const durLabel = voiceDurLabel;
    document.getElementById('inbox-voice-attachment')?.classList.add('hidden'); _refreshPopOut();
    previewBody = previewBody || durLabel;

    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';

      // ⚠️ DO NOT UPLOAD STRAIGHT TO SUPABASE AND SAVE A ONE-YEAR LINK. Every list
      // endpoint re-signs a voice note from its STORAGE KEY, so a note saved with a
      // link and no key goes silent the day that link expires (it did this for
      // every voice note recorded here). Ask message-upload-url for the address,
      // the same way the floating chat window and the coach app do, and send the
      // key it hands back along with the link.
      const urlRes = await fetch('/.netlify/functions/message-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ fileName: `voice-${Date.now()}.${ext}`, contentType: blob.type, kind: 'voice' }),
      });
      const urlJson = await urlRes.json();
      if (!urlRes.ok) throw new Error(urlJson.error || 'Failed to get upload URL');

      const putRes = await fetch(urlJson.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type },
        body: blob,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

      // R2 pre-signs the download link on the server. The older Supabase storage
      // can only sign once the file exists, and has no key to re-sign from.
      let voiceUrl = urlJson.publicUrl;
      if (urlJson.needsPostUploadSign) {
        const { data: signedData, error: signErr } = await supabase.storage
          .from(urlJson.storageBucket || 'coach-videos')
          .createSignedUrl(urlJson.path, 365 * 24 * 60 * 60);
        if (signErr) throw new Error(signErr.message);
        voiceUrl = signedData.signedUrl;
      }

      let url, payload;
      if (convType === 'group') {
        url = '/.netlify/functions/messages-group-send';
        payload = { groupId, body: durLabel, senderRole: 'coach', voiceUrl };
      } else {
        url = '/.netlify/functions/messages-send';
        payload = { clientId, body: durLabel, senderRole: 'coach', voiceUrl };
      }
      if (urlJson.storageProvider === 'r2' && urlJson.storageBucket && urlJson.storageKey) {
        payload.storageProvider = 'r2';
        payload.storageBucket = urlJson.storageBucket;
        payload.voiceStorageKey = urlJson.storageKey;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw notSent(await whyNotSent(res));
      const json = await res.json();
      if (json.message) {
        if (stillOpen() && !_activeMessages.some(m => m.id === json.message.id)) {
          if (convType === 'group') json.message.sender_name = 'You';
          _activeMessages.push(json.message);
        }
        lastCreatedAt = json.message.created_at;
      }
    } catch (err) {
      console.error('Voice send error:', err);
      // ⚠️ THE RECORDING COMES BACK. It was taken out of the composer before the
      // send, so a failure lost it and the coach had to record it all again.
      // Not over a new recording made while this one was out, and never into
      // another conversation, where Send would deliver it to someone else.
      if (stillOpen()) {
        if (!_inboxVoiceBlob) {
          _inboxVoiceBlob = blob;
          _inboxVoiceDurLabel = durLabel;
          const label = document.getElementById('inbox-voice-attachment-label');
          if (label) label.textContent = durLabel;
          document.getElementById('inbox-voice-attachment')?.classList.remove('hidden'); _refreshPopOut();
        }
        showToast(sayWhy(err), 'error');
      } else {
        showToast(notSentTo({ who: sentToName, kind: 'voice message', why: sayWhy(err) }), 'error');
      }
    }
  }

  // Send text if present (a group attachment also sends, body optional —
  // the server falls back to the filename so validation passes).
  if (textBody || hasGroupFile) {
    try {
      // The channel, like the conversation, is the one Send was pressed on
      // (read at the top). Opening another conversation resets it to in-app.
      let url, payload;
      if (convType === 'group') {
        url = '/.netlify/functions/messages-group-send';
        payload = { groupId, body: textBody, senderRole: 'coach' };
        // Reply-to: the Reply button works in a group too. It showed
        // "Replying to…" here but sent nothing, so the reply landed bare.
        if (replyTarget) payload.replyToId = replyTarget.id;
        if (groupFile) {
          payload.attachmentUrl = groupFile.url;
          payload.attachmentMeta = groupFile.meta;
          // R2-backed uploads carry storage metadata so messages-group-list
          // can re-sign expiring URLs (same as the 1:1 path).
          if (groupFile.storage) {
            payload.storageProvider = groupFile.storage.provider;
            payload.storageBucket = groupFile.storage.bucket;
            payload.attachmentStorageKey = groupFile.storage.key;
          }
        }
      } else {
        url = '/.netlify/functions/messages-send';
        payload = { clientId, body: textBody, senderRole: 'coach', channel: selectedChannel };
        // Reply-to: attach if coach clicked "Reply" on an existing message,
        // and only on the in-app channel (email/SMS use their own reply UX).
        if (replyTarget && selectedChannel === 'in_app') {
          payload.replyToId = replyTarget.id;
        }
        // Add email subject + HTML body + attachments if sending email
        if (selectedChannel === 'email') {
          if (emailSubject) payload.subject = emailSubject;
          if (htmlBody) payload.html = htmlBody;
          if (emailAttachments.length > 0) payload.attachments = _attachmentsForRequest(emailAttachments);
        }
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // Say why (send-failure.js); the catch below shows it and puts the work back.
        throw notSent(await whyNotSent(res));
      }
      const json = await res.json();
      if (json.message) {
        if (stillOpen() && !_activeMessages.some(m => m.id === json.message.id)) {
          if (convType === 'group') json.message.sender_name = 'You';
          _activeMessages.push(json.message);
        }
        lastCreatedAt = json.message.created_at;
        previewBody = textBody
          || (hasGroupFile ? `📎 ${groupFile.meta?.filename || 'Attachment'}` : textBody);
        // Only the file this send carried. If the coach has moved on and
        // attached another one elsewhere, that one stays.
        if (hasGroupFile && _pendingGroupFile === groupFile) _clearGroupFile();
        // An email "Write email" seeded from the draft: the draft has gone.
        if (seededDraft != null && getDraft(draftKey) === seededDraft) {
          clearDraft(draftKey);
          if (_emailSeededFrom?.key === draftKey) _emailSeededFrom = null;
          const ta = document.getElementById('uc-textarea');
          if (stillOpen() && ta && ta.value === seededDraft) { ta.value = ''; _resetComposerHeight(); }
        }
      }
    } catch (err) {
      const why = sayWhy(err);
      if (stillOpen()) {
        showToast(why, 'error');
        // ⚠️ AN EMAIL'S SUBJECT AND WORDS COME BACK TOO. Only the plain text box
        // below was ever put back, and an email is written in the editor, so a
        // failed email wiped everything the coach had typed (audit, 2026-10-09).
        // Never over a new email the coach started while this one was out.
        if (isEmail && document.getElementById('uc-wrap')?.dataset?.channel === 'email') {
          if (subjectField && !subjectField.value.trim()) subjectField.value = emailSubject;
          if (_emailEditor && !_emailEditor.getText().trim()) _emailEditor.setContent(htmlBody);
        }
        // Send failed — put the message back, and re-grow with it. Restoring
        // `value` alone would leave a multi-line draft crammed into one line.
        // It is this conversation's draft again, too.
        // ⚠️ Never over words typed since the press: the failed ones go above them.
        if (input) { input.value = addUnder(textBody, input.value); _autogrowComposer(input); setDraft(draftKey, input.value); }
        // The attached files come back too, so Send again carries them, into
        // this client's email composer only.
        if (emailAttachments.length && document.getElementById('uc-wrap')?.dataset?.channel === 'email') {
          _emailAttachments = emailAttachments.concat(_emailAttachments);
          _renderAttachmentPills();
        }
      } else {
        // ⚠️ ANOTHER CONVERSATION IS OPEN NOW: nothing goes into its box (see
        // the warning at stillOpen). The words ride in the toast, left up long
        // enough to read, with a Copy message button, under the name of the
        // client they were for. And they go back to that conversation's own
        // draft, unless something newer was typed there since.
        if (input && textBody && !getDraft(draftKey)) setDraft(draftKey, textBody);
        const kind = convType === 'group' ? 'message'
          : selectedChannel === 'email' ? 'email'
          : selectedChannel === 'sms' ? 'text message'
          : 'message';
        if (textBody) showToast(notSentTo({ who: sentToName, kind, why, words: textBody }), 'error', wordsToastOptions(textBody));
        else showToast(notSentTo({ who: sentToName, kind, why }), 'error');
      }
    }
  }

  // Clear reply-to chip after a successful send (only if send actually
  // landed — on error we keep the chip so the coach can retry). Only the
  // quote this send carried: a reply the coach has lined up since, in the
  // conversation open now, stays.
  if (lastCreatedAt && replyTarget && _replyToTarget === replyTarget) _dropReplyTarget();

  renderChatMessages();

  // Update the preview line of the conversation this went to.
  if (lastCreatedAt) {
    const conv = convType === 'group'
      ? convStore.list().find(c => c.type === 'group' && c.groupId === groupId)
      : convStore.list().find(c => c.type === 'direct' && c.clientId === clientId);
    if (conv) { conv.lastMessage = { body: previewBody, senderRole: 'coach', createdAt: lastCreatedAt }; renderConversationsList(); }
  }

  btn.disabled = false;
  input?.focus();
};

// Given a raw message row from realtime (which has `reply_to_id` but no
// resolved `reply_to` object), attach a derived reply_to preview by
// looking up the referenced message in the already-loaded thread. If
// the referenced message isn't loaded, reply_to stays undefined and the
// next poll/refetch will hydrate it from the backend.
function _resolveReplyToInPlace(msg) {
  if (!msg?.reply_to_id || msg.reply_to) return;
  const ref = _activeMessages.find(m => m.id === msg.reply_to_id);
  if (!ref) return;
  // An unsent message is quoted as "Message unsent", as the server does.
  // (Its words are already gone from the page, so this would read empty.)
  if (ref.deleted_at) { msg.reply_to = unsentQuote(ref); return; }
  const previewBody = ref.voice_url ? 'Voice message'
    : ref.attachment_url ? (ref.attachment_meta?.filename || 'Attachment')
    : (ref.body || '');
  msg.reply_to = {
    id: ref.id,
    sender_role: ref.sender_role,
    preview: previewBody.slice(0, 140),
    is_voice: !!ref.voice_url,
    is_attachment: !!ref.attachment_url,
  };
}

// Realtime subscription + status tracking is owned by the realtime
// factory above; initInboxRealtime is re-exported from there.

// The sidebar number (and the Unread tab's, and the browser tab's) is the
// shared store's: convStore.writeNumber counts the people waiting (D7).

// ── Render inbox (called by navigation) ──────────────────────
// Called when navigating away from inbox
export function deactivateInbox() {
  _stopMessagePoll();
  closeMessageMenus();
  // ⚠️ LET GO OF THE OPEN CONVERSATION'S LIVE CHANNEL. Leaving the page used to
  // keep the last one-to-one or group channel open for good. The shared
  // registry (messages/channel-registry.js) removes it once no screen holds
  // it, so a corner chat on the same person keeps it.
  unsubscribeActiveClientTyping();
  _unsubscribeGroupChannel();
  _dropReplyTarget();
  _discardPendingVoice();
  _activeClientId = null;
  _activeGroupId = null;
  _activeConvType = null;
  _activeGroupMembers = [];
  document.getElementById('inbox-list-pane')?.classList.remove('conv-open');
  document.getElementById('inbox-chat-pane')?.classList.add('hidden');
}

// ── Unified composer channel switching ───────────────────────
const UC_ICONS = {
  in_app: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>',
  sms: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>',
  email: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
};
const UC_LABELS = { in_app: 'In-App', sms: 'SMS', email: 'Email' };

// ── Channel capability gating ────────────────────────────────
// Disables SMS / Email / Call composer options when the underlying
// channel isn't ready. The check itself (a number with A2P approved for
// texts, a verified domain for email) is shared with the corner messenger:
// messages/comms-caps.js. window._invalidateCommsCaps lives there too; when
// it forgets the answer, the channel menu is dimmed again from a fresh read.
onCommsCapsInvalidated(() => { _applyComposerGating(); });

async function _applyComposerGating() {
  const caps = await getCommsCaps();
  const opts = document.querySelectorAll('.uc-channel-option');
  opts.forEach(btn => {
    const ch = btn.getAttribute('data-ch');
    if (ch === 'in_app') return; // always available
    const enabled = ch === 'email' ? caps.email : ch === 'sms' ? caps.sms : true;
    btn.classList.toggle('uc-channel-option-disabled', !enabled);
    btn.disabled = !enabled;
    if (!enabled) {
      btn.setAttribute('title', caps.reasons[ch] || 'Channel not configured');
      btn.style.opacity = '0.45';
      btn.style.cursor = 'not-allowed';
    } else {
      btn.removeAttribute('title');
      btn.style.opacity = '';
      btn.style.cursor = '';
    }
  });
}
// Re-apply gating whenever the channel menu is opened (cheap; cache hits
// after the first call).
document.addEventListener('click', (e) => {
  if (e.target.closest?.('#uc-channel-trigger')) {
    _applyComposerGating();
  }
});

window._setComposerChannel = function(ch) {
  const wrap = document.getElementById('uc-wrap');
  if (!wrap) return;

  // Block selection of channels the coach hasn't set up yet.
  const caps = peekCommsCaps();
  if (caps) {
    if (ch === 'email' && !caps.email) {
      showToast(caps.reasons.email || 'Email channel not set up.', 'error');
      return;
    }
    if (ch === 'sms' && !caps.sms) {
      showToast(caps.reasons.sms || 'SMS channel not set up.', 'error');
      return;
    }
  }

  wrap.dataset.channel = ch;

  // Close menu
  document.getElementById('uc-channel-menu')?.classList.add('hidden');

  // Update trigger label + icon
  const iconEl = document.getElementById('uc-ch-icon');
  const labelEl = document.getElementById('uc-ch-label');
  if (iconEl) iconEl.outerHTML = `<span class="uc-ch-icon" id="uc-ch-icon">${UC_ICONS[ch] || ''}</span>`;
  if (labelEl) labelEl.textContent = UC_LABELS[ch] || ch;

  // Update active state in menu
  document.querySelectorAll('.uc-channel-option').forEach(o => o.classList.toggle('active', o.dataset.ch === ch));

  // Toggle fields
  document.getElementById('uc-fields-sms')?.classList.toggle('hidden', ch !== 'sms');
  document.getElementById('uc-fields-email')?.classList.toggle('hidden', ch !== 'email');

  // Toggle textarea vs TipTap editor
  const ta = document.getElementById('uc-textarea');
  const edCon = document.getElementById('uc-editor-container');
  if (ch === 'email') {
    if (ta) ta.classList.add('hidden');
    if (edCon) edCon.classList.remove('hidden');
    // Populate email fields
    _populateUcEmailFields();
    // Init TipTap if needed
    if (!_emailEditor) {
      injectEditorCSS();
      createEmailEditor('uc-editor-container', { placeholder: 'Compose your email...' }).then(ed => {
        _emailEditor = ed;
        // Every change to the email, typed, pasted, filled or emptied, is one
        // TipTap transaction: Pop out hides or shows with it.
        ed?.editor?.on?.('transaction', _refreshPopOut);
        _emailSend.applyPendingDraft(); // a draft carried in from the corner
      });
    }
  } else {
    if (ta) ta.classList.remove('hidden');
    if (edCon) edCon.classList.add('hidden');
    if (ch === 'sms') _populateUcSmsFields();
    const placeholders = { in_app: 'Type a message...', sms: 'Type an SMS...' };
    if (ta) ta.placeholder = placeholders[ch] || 'Type a message...';
  }

  // Show/hide voice button (hide for email) and attach button (show only for email)
  document.getElementById('uc-voice-btn')?.classList.toggle('hidden', ch === 'email');
  document.getElementById('uc-attach-btn')?.classList.toggle('hidden', ch !== 'email');
  // Clear attachments when switching away from email
  if (ch !== 'email') _clearEmailAttachments();
  _refreshPopOut();
};

function _populateUcEmailFields() {
  const settings = JSON.parse(localStorage.getItem('revfit_settings') || '{}');
  const businessName = settings.emailBusinessName || 'Your Business';
  const domain = emailDomainVerified(settings) ? settings.emailDomain : '';
  const emailUser = (currentUser?.email || '').split('@')[0].replace(/[^a-z]/gi, '').toLowerCase();
  const fromEmail = domain && emailUser ? `${emailUser}@${domain}` : (domain ? `notifications@${domain}` : currentUser?.email || '');

  const fromEl = document.getElementById('uc-email-from');
  const fromNameEl = document.getElementById('uc-email-from-name');
  if (fromEl) fromEl.textContent = fromEmail;
  if (fromNameEl) fromNameEl.textContent = businessName;

  const client = convStore.roster().find(c => c.id === _activeClientId);
  const clientEmail = client?.email || '';
  const clientName = client?.name || client?.data?.name || '';
  const toEl = document.getElementById('uc-email-to');
  if (toEl) toEl.textContent = clientEmail ? `${clientName} <${clientEmail}>` : clientName || 'No email on file';
}

// The coach's texting number is kept with the setup check (comms-caps.js).
async function _populateUcSmsFields() {
  const fromEl = document.getElementById('uc-sms-from');
  const toEl = document.getElementById('uc-sms-to');
  // Get Twilio number
  if (!getCachedTwilioNumber()) {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/coach-channels-get', {
        headers: { Authorization: 'Bearer ' + session.access_token },
      });
      if (res.ok) {
        const { channels } = await res.json();
        const sms = channels?.find(c => c.channel === 'sms');
        setCachedTwilioNumber(sms?.twilio_phone_number || '');
      }
    } catch {}
  }
  if (fromEl) fromEl.textContent = _fmtPhone(getCachedTwilioNumber()) || 'Not configured';
  // Get client phone
  const client = convStore.roster().find(c => c.id === _activeClientId);
  const clientPhone = client?.phone || client?.data?.phone || '';
  if (toEl) toEl.textContent = _fmtPhone(clientPhone) || 'No phone on file';
}

// Close dropdowns when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('.uc-channel-dropdown')) document.getElementById('uc-channel-menu')?.classList.add('hidden');
  if (!e.target.closest('.uc-send-split')) document.getElementById('uc-send-menu')?.classList.add('hidden');
});

// ── Click-to-call ──────────────────────────────────────────────
// The question and the request are shared with the corner messenger
// (messages/client-call.js). The Inbox's names call the open client.
window._confirmCall = () => confirmCall(_activeClientId);
window.initiateCall = () => initiateCall(_activeClientId);

export function renderInbox() {
  if (convStore.list().length === 0) {
    loadInboxConversations();
  } else {
    // ⚠️ FROM THE SHARED LIST AS IT IS NOW. The corner reads, archives and marks
    // chats unread there while the Inbox is not showing; redrawn from the filtered
    // copy made before, a chat read in the corner stayed under Unread, an archived
    // one under All, and one marked unread was missing, until the next poll.
    _applyFilter();
    renderConversationsList();
  }
  _loadEmojiRanking();
}

// ── Poll conversation list for new unread messages ────────────
// Realtime (postgres_changes) is the primary delivery path — the
// conversation list updates the moment a new message arrives. This
// poll is a safety net for when realtime drops; 60s is plenty.
// Was 8s, which generated ~450 calls/hr × 7 queries each = 3,150
// DB reads/hr per coach just from this one interval. Way too hot.
// The interval itself is the shared store's (one poll for every screen).
export function startInboxConvPoll() {
  convStore.startPoll(loadInboxConversations);
}
export function stopInboxConvPoll() {
  convStore.stopPoll();
}

// ── Full cleanup (call on logout) ────────────────────────────
export function cleanupInbox() {
  _stopMessagePoll();
  closeMessageMenus();
  stopInboxConvPoll();
  _realtime.unsubscribeRealtime();
  unsubscribeActiveClientTyping();
  _unsubscribeGroupChannel();
  _dropReplyTarget();
  _discardPendingVoice();
  _activeClientId = null;
  _activeGroupId = null;
  _activeConvType = null;
  _activeMessages = [];
  _activeGroupMembers = [];
  convStore.clear();
  convStore.notify(); // no number is left on the page for the next person
  _filteredConversations = [];
  _hasOlder = false;
  _loadingOlder = false;
  _threadSeq++;
}

// ── Voice Playback ──────────────────────────────────────────
// The bubbles' player (window.playVoiceMsg) is shared: messages/voice.js.

// ── Voice Recording ─────────────────────────────────────────
// The recorder itself is shared (messages/voice.js), and only one recording
// runs anywhere: while a corner chat records, the Inbox mic is refused, and
// the reverse. This is the Inbox's screen for it: the red mic, the recording
// bar and its timer. The Inbox records as the owner 'inbox'.
const INBOX_VOICE = 'inbox';

// The mic button on the page is #uc-voice-btn. This used to look for
// #inbox-voice-btn (an id nothing has), so the button never showed it was
// recording, and a second press called window.stopVoiceRecording, which does
// not exist anywhere and threw while the recording ran on. A second press now
// does what the Stop button does.
function _showMicRecording(on) {
  const btn = document.getElementById('uc-voice-btn');
  if (!btn) return;
  btn.classList.toggle('recording', on);
  btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  btn.title = on ? 'Stop recording' : 'Record voice message';
}

// The open chat's first name (a group's name), so a corner chat that wants
// the microphone is told where the recording is.
function _voiceOwnerName() {
  const name = _conversationName(_activeConvType, _activeClientId, _activeGroupId);
  return _activeConvType === 'group' ? name : (name.split(/\s+/)[0] || name);
}

// The bar's timer follows the shared recording.
onRecordingChange(({ owner, elapsed }) => {
  // A recording starting or stopping shows or hides Pop out (_popOutWouldDrop).
  _refreshPopOut();
  if (owner !== INBOX_VOICE) return;
  const timerEl = document.getElementById('inbox-voice-timer');
  if (timerEl) timerEl.textContent = `${Math.floor(elapsed / 60)}:${(elapsed % 60).toString().padStart(2, '0')}`;
});

window.toggleVoiceRecording = async function() {
  if (isRecording(INBOX_VOICE)) {
    window.finishVoiceRecording();
    return;
  }

  const started = await startRecording(INBOX_VOICE, { name: _voiceOwnerName() });
  if ('busyWith' in started) {
    // A second press while the browser is still asking for the microphone.
    if (started.busyWith === INBOX_VOICE) return;
    showToast(busyWords(started.name), 'error');
    return;
  }
  // ⚠️ SAID ON THE PAGE, NEVER WITH alert() (a browser alert stops the whole
  // page until it is dismissed). Thrown away while the browser asked: nothing to say.
  if ('error' in started) {
    if (started.error === 'unsupported') showToast(RECORDING_UNSUPPORTED, 'error');
    else if (started.error === 'blocked') showToast(MIC_BLOCKED, 'error');
    return;
  }

  // Show recording UI
  _showMicRecording(true);
  document.getElementById('inbox-voice-bar')?.classList.remove('hidden');
};

let _inboxVoiceBlob = null;
let _inboxVoiceDurLabel = '';

// ── Email Attachments ────────────────────────────────────────
let _emailAttachments = []; // [{file, name, size, type, base64}]
// A group file uploads to storage on its own, so it keeps a 10MB limit.
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

// ⚠️ AN EMAIL'S FILES RIDE INSIDE THE REQUEST (base64, in the payload to
// messages-send or scheduled-email-create). The limit was 10MB a file and 25MB
// in all, but a request takes about 6MB, so past about 4.5MB of files the
// platform refused the email with no words. 4MB in all now, from
// send-failure.js, where the server's copy is held to the same number.
function _emailFilesBytes(list) {
  return list.reduce((s, a) => s + a.size, 0);
}
function _emailFilesTooBig(list) {
  const total = _emailFilesBytes(list);
  if (total <= EMAIL_FILES_LIMIT_BYTES) return '';
  return `These files add up to ${_fmtFileSize(total)}. ${EMAIL_FILES_LIMIT_WORDS} Remove some and try again.`;
}
function _attachmentsForRequest(list) {
  return list.map(a => ({ filename: a.name, content: a.base64, type: a.type, size: a.size }));
}
// "a.pdf", "a.pdf and b.pdf", "a.pdf, b.pdf and c.pdf"
function _namesInWords(names) {
  return names.length < 2 ? (names[0] || '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

window._addEmailAttachment = function() {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.zip';
  input.onchange = async () => {
    const files = Array.from(input.files || []);
    // A file that would take the email past the limit is left out and named;
    // a smaller one after it can still fit.
    const notAdded = [];
    for (const file of files) {
      if (_emailFilesBytes(_emailAttachments) + file.size > EMAIL_FILES_LIMIT_BYTES) {
        notAdded.push(file.name);
        continue;
      }
      const base64 = await _fileToBase64(file);
      _emailAttachments.push({ name: file.name, size: file.size, type: file.type, base64 });
    }
    _renderAttachmentPills();
    if (notAdded.length) {
      showToast(`${_namesInWords(notAdded)} ${notAdded.length === 1 ? 'was' : 'were'} not added. ${EMAIL_FILES_LIMIT_WORDS}`, 'error');
    }
  };
  input.click();
};

window._removeEmailAttachment = function(idx) {
  _emailAttachments.splice(idx, 1);
  _renderAttachmentPills();
};

function _fileToBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(',')[1]; // strip data:...;base64, prefix
      resolve(b64);
    };
    reader.readAsDataURL(file);
  });
}

// _fmtFileSize imported from ./inbox/format-helpers.js as formatFileSize.

function _renderAttachmentPills() {
  _refreshPopOut();
  const container = document.getElementById('uc-attachments');
  if (!container) return;
  if (_emailAttachments.length === 0) { container.classList.add('hidden'); container.innerHTML = ''; return; }
  container.classList.remove('hidden');
  container.innerHTML = _emailAttachments.map((a, i) => `
    <div class="uc-attachment-pill">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
      <span class="uc-attachment-name">${escHtml(a.name)}</span>
      <span class="uc-attachment-size">${_fmtFileSize(a.size)}</span>
      <button class="uc-attachment-remove" onclick="window._removeEmailAttachment(${i})" title="Remove">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  `).join('');
}

// ── Group attachments (photo / GIF / file) ───────────────────
// The group composer is in-app only, so files upload to storage via
// message-upload-url (same flow as the 1:1 chat widget) and ride the
// messages-group-send payload — parity with what mobile clients already
// send and render in groups. One pending file at a time.
//
// ⚠️ A FILE BELONGS TO THE GROUP IT WAS PICKED IN. The upload takes a while,
// and the coach could open another group before it ended; when it finished
// it was lined up in whatever group was open by then, and went out there
// with the next message. Each pick is numbered, and every change of
// conversation (another group, a client, back to the list) drops the pick,
// as do the × and a newer pick. An upload whose pick was dropped is thrown
// away, so a file can only ever land in the group it was picked in.
let _pendingGroupFile = null; // { url, meta, storage } once the upload settles
let _groupFileUploading = false;
let _groupFileSeq = 0; // numbers each pick; bumped when the pending file is dropped

// The composer's single attach button routes by context: group conversation
// → storage-backed group attachment; otherwise the email base64 flow.
window._composerAttachClick = function() {
  if (_activeConvType === 'group') return _pickGroupFile();
  return window._addEmailAttachment();
};

function _renderGroupFilePill(label, uploading) {
  _refreshPopOut();
  const container = document.getElementById('uc-group-attachment');
  if (!container) return;
  if (!label) { container.classList.add('hidden'); container.innerHTML = ''; return; }
  container.classList.remove('hidden');
  container.innerHTML = `
    <div class="uc-attachment-pill">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
      <span class="uc-attachment-name">${escHtml(label)}</span>
      ${uploading ? '<span class="uc-attachment-size">uploading…</span>' : ''}
      <button class="uc-attachment-remove" onclick="window._removeGroupFileAttachment()" title="Remove">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>`;
}

function _clearGroupFile() {
  _groupFileSeq++;
  _pendingGroupFile = null;
  _groupFileUploading = false;
  _renderGroupFilePill(null);
}
window._removeGroupFileAttachment = _clearGroupFile;

async function _pickGroupFile() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) {
      showToast('File too large. Maximum 10MB.', 'error');
      return;
    }
    // This pick's number (see the warning at _pendingGroupFile).
    const seq = ++_groupFileSeq;
    _groupFileUploading = true;
    _renderGroupFilePill(file.name, true);
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/message-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ fileName: file.name, contentType: file.type }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Upload URL failed');

      await fetch(json.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      // Supabase private buckets can only sign the download URL once the
      // object exists; R2 pre-signs server-side so publicUrl is already set.
      let finalUrl = json.publicUrl;
      if (json.needsPostUploadSign) {
        const bucket = json.storageBucket || 'coach-videos';
        const { data: signed, error: signErr } = await supabase.storage
          .from(bucket)
          .createSignedUrl(json.path, 31536000);
        if (signErr) throw new Error('Signing failed: ' + signErr.message);
        finalUrl = signed.signedUrl;
      }

      // ⚠️ Dropped mid-upload: it is not for whoever is open now.
      if (seq !== _groupFileSeq) return;
      _pendingGroupFile = {
        url: finalUrl,
        meta: { filename: file.name, type: file.type, size: file.size },
        storage: (json.storageProvider === 'r2' && json.storageBucket && json.storageKey)
          ? { provider: 'r2', bucket: json.storageBucket, key: json.storageKey }
          : null,
      };
      _groupFileUploading = false;
      _renderGroupFilePill(file.name, false);
    } catch (err) {
      // A dropped file's failure says nothing, and must not clear the box:
      // the pill there may be another file's by now.
      if (seq !== _groupFileSeq) return;
      showToast('Upload failed: ' + err.message, 'error');
      _clearGroupFile();
    }
  };
  input.click();
}

function _clearEmailAttachments() {
  _emailAttachments = [];
  _renderAttachmentPills();
}

window.cancelVoiceRecording = function() {
  cancelRecording(INBOX_VOICE);
  _inboxVoiceBlob = null;
  _inboxVoiceDurLabel = '';
  _cleanupVoiceUI();
};

// Stop recording — show attachment pill in composer
window.finishVoiceRecording = function() {
  if (!isRecording(INBOX_VOICE)) return;

  _inboxVoiceDurLabel = recordingDurationLabel();

  finishRecording(INBOX_VOICE, (blob) => {
    if (!blob) { _cleanupVoiceUI(); return; }
    _inboxVoiceBlob = blob;
    _cleanupVoiceUI();

    // Show attachment pill
    const pill = document.getElementById('inbox-voice-attachment');
    const label = document.getElementById('inbox-voice-attachment-label');
    if (pill) pill.classList.remove('hidden');
    if (label) label.textContent = _inboxVoiceDurLabel;
  });
};

let _inboxPreviewAudio = null;

window.previewVoiceAttachment = function() {
  if (!_inboxVoiceBlob) return;

  const btn = document.getElementById('inbox-voice-attachment-play');
  const playIcon = btn?.querySelector('.inbox-att-play-icon');
  const pauseIcon = btn?.querySelector('.inbox-att-pause-icon');

  if (_inboxPreviewAudio) {
    _inboxPreviewAudio.pause();
    _inboxPreviewAudio = null;
    if (playIcon) playIcon.style.display = '';
    if (pauseIcon) pauseIcon.style.display = 'none';
    return;
  }

  const url = URL.createObjectURL(_inboxVoiceBlob);
  _inboxPreviewAudio = new Audio(url);

  if (playIcon) playIcon.style.display = 'none';
  if (pauseIcon) pauseIcon.style.display = '';

  _inboxPreviewAudio.onended = () => {
    _inboxPreviewAudio = null;
    URL.revokeObjectURL(url);
    if (playIcon) playIcon.style.display = '';
    if (pauseIcon) pauseIcon.style.display = 'none';
  };

  _inboxPreviewAudio.play().catch(() => {});
};

function _stopInboxPreview() {
  if (_inboxPreviewAudio) {
    _inboxPreviewAudio.pause();
    _inboxPreviewAudio = null;
  }
  const btn = document.getElementById('inbox-voice-attachment-play');
  const playIcon = btn?.querySelector('.inbox-att-play-icon');
  const pauseIcon = btn?.querySelector('.inbox-att-pause-icon');
  if (playIcon) playIcon.style.display = '';
  if (pauseIcon) pauseIcon.style.display = 'none';
}

window.removeVoiceAttachment = function() {
  _stopInboxPreview();
  _inboxVoiceBlob = null;
  _inboxVoiceDurLabel = '';
  document.getElementById('inbox-voice-attachment')?.classList.add('hidden'); _refreshPopOut();
};

function _cleanupVoiceUI() {
  _refreshPopOut();
  _showMicRecording(false);
  document.getElementById('inbox-voice-bar')?.classList.add('hidden');
  const timerEl = document.getElementById('inbox-voice-timer');
  if (timerEl) timerEl.textContent = '0:00';
  const dot = document.getElementById('inbox-voice-dot');
  if (dot) dot.style.background = '';
  const label = document.getElementById('inbox-voice-label');
  if (label) label.textContent = 'Recording...';
  document.getElementById('inbox-voice-stop-rec')?.classList.remove('hidden');
  document.getElementById('inbox-voice-send')?.classList.add('hidden');
}

// ── Inline Scheduled Emails (GHL-style) ──────────────────────
let _pendingScheduled = []; // pending scheduled for active conversation

// The list was emptied when the chat opened (openInboxConversation), so this
// never empties it again: by now it may be the list of a chat opened since.
// ⚠️ The coach may open another chat while this loads: only the client still
// on screen takes the answer. A late one used to land inside the group opened
// since, Cancel and all.
async function _loadScheduledForClient(clientId, accessToken) {
  try {
    const res = await fetch(`/.netlify/functions/scheduled-email-list?clientId=${clientId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    if (_activeConvType !== 'direct' || _activeClientId !== clientId) return;
    _pendingScheduled = (json.scheduled || []).filter(s => s.status === 'pending');
    _renderInlineScheduled();
  } catch {}
}

// A group's own pending Send Later messages (scheduled-email-list?groupId=).
// ⚠️ The coach may open another chat while this loads: only the group still
// on screen takes the answer.
async function _loadScheduledForGroup(groupId) {
  try {
    const { data: { session } } = await getSupabase().auth.getSession();
    if (!session) return;
    const res = await fetch(`/.netlify/functions/scheduled-email-list?groupId=${encodeURIComponent(groupId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) return;
    const json = await res.json();
    if (_activeConvType !== 'group' || _activeGroupId !== groupId) return;
    _pendingScheduled = (json.scheduled || []).filter(s => s.status === 'pending');
    _renderInlineScheduled();
  } catch {}
}

function _buildInlineScheduledHtml() {
  if (_pendingScheduled.length === 0) return '';
  return _pendingScheduled.map(s => {
    const dt = new Date(s.scheduledFor);
    const timeStr = dt.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
    const dateStr = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const chLabel = s.channel === 'sms' ? 'SMS' : s.channel === 'email' ? 'Email' : 'Message';
    const preview = s.subject ? `${s.subject} — ${s.body}` : s.body;
    return `
      <div class="inbox-sched-inline">
        <div class="inbox-sched-banner">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span>Send Later</span>
          <span class="inbox-sched-time">${timeStr} on ${dateStr}</span>
          <button class="inbox-sched-cancel" onclick="window._cancelInlineScheduled('${s.id}')">Cancel</button>
        </div>
        <div class="inbox-sched-preview">
          <span class="inbox-conv-ch inbox-conv-ch--${s.channel === 'sms' ? 'sms' : s.channel === 'email' ? 'email' : ''}" style="margin-right:6px;">${chLabel}</span>
          ${escHtml((preview || '').slice(0, 120))}
        </div>
      </div>`;
  }).join('');
}

function _renderInlineScheduled() {
  // Re-render chat messages which now includes inline scheduled
  renderChatMessages();
}

window._cancelInlineScheduled = async function(id) {
  try {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/scheduled-email-cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ id }),
    });
    if (!res.ok) throw new Error('Failed');
    _pendingScheduled = _pendingScheduled.filter(s => s.id !== id);
    _renderInlineScheduled();
    showToast('Scheduled message cancelled', 'success');
  } catch {
    showToast('Failed to cancel', 'error');
  }
};

// ── Scheduled Messages Queue ─────────────────────────────────
window._showScheduledQueue = async function() {
  const overlay = document.createElement('div');
  overlay.className = 'inbox-email-modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  overlay.innerHTML = `
    <div class="inbox-email-modal" style="max-width:620px;">
      <div class="inbox-email-modal-header">
        <div class="inbox-email-modal-subject">Scheduled Messages</div>
        <button class="inbox-email-modal-close" onclick="this.closest('.inbox-email-modal-overlay').remove()">&times;</button>
      </div>
      <div class="sched-queue-tabs">
        <button class="sched-queue-tab active" data-tab="pending" onclick="window._filterScheduledTab(this,'pending')">Pending</button>
        <button class="sched-queue-tab" data-tab="sent" onclick="window._filterScheduledTab(this,'sent')">Sent</button>
        <button class="sched-queue-tab" data-tab="cancelled" onclick="window._filterScheduledTab(this,'cancelled')">Cancelled</button>
      </div>
      <div class="sched-queue-body" id="sched-queue-body">
        <div style="padding:40px;text-align:center;color:var(--text-muted);">Loading...</div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  // Fetch
  try {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/scheduled-email-list', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) throw new Error('Failed');
    const json = await res.json();
    window._scheduledItems = json.scheduled || [];
    window._scheduledFilter = 'pending';
    _renderScheduledQueue();
  } catch {
    document.getElementById('sched-queue-body').innerHTML = '<div style="padding:40px;text-align:center;color:var(--danger);">Failed to load.</div>';
  }
};

window._filterScheduledTab = function(btn, tab) {
  btn.closest('.sched-queue-tabs').querySelectorAll('.sched-queue-tab').forEach(t => t.classList.remove('active'));
  btn.classList.add('active');
  window._scheduledFilter = tab;
  _renderScheduledQueue();
};

function _renderScheduledQueue() {
  const body = document.getElementById('sched-queue-body');
  if (!body) return;
  const filter = window._scheduledFilter || 'pending';
  const items = (window._scheduledItems || []).filter(i => i.status === filter);

  if (items.length === 0) {
    const labels = { pending: 'No pending scheduled messages.', sent: 'No sent scheduled messages.', cancelled: 'No cancelled messages.' };
    body.innerHTML = `<div style="padding:40px;text-align:center;color:var(--text-muted);font-size:13px;">${labels[filter]}</div>`;
    return;
  }

  body.innerHTML = items.map(i => {
    const dt = new Date(i.scheduledFor);
    const timeStr = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    const chBadge = i.channel === 'sms' ? '<span class="inbox-conv-ch inbox-conv-ch--sms">SMS</span>'
      : i.channel === 'email' ? '<span class="inbox-conv-ch inbox-conv-ch--email">Email</span>'
      : '<span class="inbox-conv-ch" style="background:rgba(99,102,241,0.1);color:var(--primary);">In-App</span>';
    const statusBadge = i.status === 'sent'
      ? '<span style="font-size:10px;font-weight:600;color:#22c55e;">Sent</span>'
      : i.status === 'cancelled'
      ? '<span style="font-size:10px;font-weight:600;color:var(--danger);">Cancelled</span>'
      : '';
    const cancelBtn = i.status === 'pending'
      ? `<button class="sched-queue-cancel" onclick="window._cancelScheduledEmail('${i.id}')">Cancel</button>`
      : '';
    const sentTime = i.sentAt ? `<span style="font-size:10px;color:var(--text-muted);margin-left:8px;">Sent ${new Date(i.sentAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>` : '';

    return `
      <div class="sched-queue-item">
        <div class="sched-queue-item-top">
          <span class="sched-queue-client">${escHtml(i.clientName)}</span>
          ${chBadge}
          ${statusBadge}${sentTime}
          <span class="sched-queue-time">${timeStr}</span>
        </div>
        <div class="sched-queue-item-body">
          ${i.subject ? `<strong>${escHtml(i.subject)}</strong> — ` : ''}${escHtml(i.body)}
        </div>
        ${cancelBtn ? `<div class="sched-queue-item-actions">${cancelBtn}</div>` : ''}
      </div>`;
  }).join('');
}

window._cancelScheduledEmail = async function(id) {
  try {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/scheduled-email-cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ id }),
    });
    if (!res.ok) throw new Error('Failed');
    // Update local state
    const item = window._scheduledItems?.find(i => i.id === id);
    if (item) { item.status = 'cancelled'; item.cancelledAt = new Date().toISOString(); }
    _renderScheduledQueue();
    showToast('Scheduled message cancelled', 'success');
  } catch {
    showToast('Failed to cancel', 'error');
  }
};

// ── Helpers ──────────────────────────────────────────────────
// getInitials, formatRelativeTime, escHtml moved to
// inbox/format-helpers.js (imported at top).
